import html2canvas from 'html2canvas-pro';

import { ApiService } from '@/app/services/api.service';
import type {
  FeedbackCategory,
  RetentionPolicyResponse,
  WidgetConfig,
  WidgetDisplayBehavior,
  WidgetDisplayStyle,
  WidgetPosition,
  WidgetRequestDetail,
  WidgetRequestItem,
  WidgetTheme,
} from '@/app/models/feedback.model';
import widgetStyles from '@/assets/styles/styles.css?inline';

const tagName = 'feedback-widget';

type AnnotTool = 'pen' | 'highlight' | 'eraser';
type CaptureMode = 'old-basic' | 'full' | 'customized' | 'snippet' | 'upload';
type DashboardTab = 'table' | 'detail';
type WidgetSyncMessage = {
  type: 'request-created' | 'request-updated' | 'requests-refresh';
  requestId?: string;
  ts: number;
};

class FeedbackWidget extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['user-id', 'user-email', 'user-role', 'tenant-key'];
  }

  private shadowRootRef: ShadowRoot;
  private isOpen = false;
  private isSubmitting = false;
  private selectedTheme: 'light' | 'dark' = 'light';
  private followAppTheme = true;
  private appThemeObserver?: MutationObserver;
  private screenshotBlob?: Blob;
  private api?: ApiService;
  private categories: FeedbackCategory[] = [];
  private fallbackCategories: FeedbackCategory[] = [];
  private requests: WidgetRequestItem[] = [];
  private selectedRequest?: WidgetRequestDetail;
  private selectedRequestId?: string;
  private retentionPolicy?: RetentionPolicyResponse | null;
  private activeDashboardTab: DashboardTab = 'table';
  private adminActionToken?: string;
  private adminEmail = '';
  private creatorEmail = '';
  private adminPassword = '';
  private adminWidgetLoginUrl = '';
  private adminWidgetLoginTenantKey = '';
  private adminSessionActive = false;
  private canViewRequestsTab = false;
  private requestsPollTimer?: number;
  private isRefreshingRequests = false;
  private toastTimer?: number;
  private syncChannel?: BroadcastChannel;
  private requestSearchQuery = '';
  private requestStatusFilter: 'all' | 'updated' | WidgetRequestItem['status'] = 'all';
  private requestStatusFilters: string[] = ['all'];
  private requestUserFilters: string[] = ['all'];
  private requestUserFilterDraft: string[] = ['all'];
  private requestUserFilterQuery = '';
  private requestUserFilterPanelOpen = false;
  private requestStatusFilterDraft: string[] = ['all'];
  private filterUserSectionOpen = true;
  private filterStatusSectionOpen = true;
  private requestPage = 1;
  private exportBusy = false;
  private readonly requestPageSize = 5;
  private readonly requestPollIntervalMs = 180000;
  private readonly minBackgroundRefreshMs = 15000;
  private lastBackgroundRefreshAt = 0;
  private seenRequestUpdates: Record<string, string> = {};
  private seenUpdatesInitialized = false;
  private seenUpdatesStorageKey = '';
  private deleteConfirmResolver?: (confirmed: boolean) => void;
  private readonly requestStatusOptions: Array<{ key: string; label: string }> = [
    { key: 'all', label: 'All statuses' },
    { key: 'updated', label: 'New updates' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'in_review', label: 'In review' },
    { key: 'needs_info', label: 'Needs info' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  // Annotation state
  private annotTool: AnnotTool = 'pen';
  private annotColor = '#ef4444';
  private annotSize = 4;
  private annotDrawing = false;
  private annotLastX = 0;
  private annotLastY = 0;
  private annotUndoStack: ImageData[] = [];
  private captureMode: CaptureMode = 'customized';
  private displayStyle: WidgetDisplayStyle = 'text';
  private displayBehavior: WidgetDisplayBehavior = 'always-visible';
  private widgetPosition: WidgetPosition = 'bottom-right';
  private accentColor = '';
  private launcherLabel = '';
  private logoUrl = '';
  private launcherInitials = '';
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private hideTimer?: number;
  private isCapturingScreenshot = false;
  private selectedRating = 0;
  private snipDrawing = false;
  private snipStartX = 0;
  private snipStartY = 0;
  private snipSelection: { x: number; y: number; width: number; height: number } | null = null;

  constructor() {
    super();
    this.shadowRootRef = this.attachShadow({ mode: 'open' });
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.api) return;

    if (
      name === 'user-id' ||
      name === 'user-email' ||
      name === 'user-role' ||
      name === 'tenant-key'
    ) {
      this.applyUserContextFromAttributes(true);
    }
  }

  async connectedCallback(): Promise<void> {
    const config = this.readConfig();
    this.seenUpdatesStorageKey = this.buildSeenUpdatesStorageKey(
      config.tenantKey,
      config.userId,
      config.userEmail
    );
    this.loadSeenRequestUpdates();
    this.followAppTheme = config.theme !== 'light' && config.theme !== 'dark';
    this.selectedTheme = this.followAppTheme
      ? this.detectAppTheme()
      : config.theme === 'dark'
        ? 'dark'
        : 'light';
    this.setupSyncChannel(config.tenantKey);
    this.captureMode = this.readCaptureMode();
    this.fallbackCategories = config.categories ?? [];
    this.api = new ApiService({
      apiUrl: config.apiUrl,
      tenantKey: config.tenantKey,
      authToken: config.authToken,
      userId: config.userId,
      userEmail: config.userEmail,
      userRole: config.userRole,
    });
    this.applyUserContextFromAttributes();
    this.canViewRequestsTab = true;
    // Start a usage-analytics session (fire-and-forget) for the widget user.
    void this.api.initAnalyticsSession();
    this.adminEmail = config.adminEmail || this.adminEmail;
    // Tracking-only: the integration creator (main admin) email is never rendered in the widget UI.
    this.creatorEmail = config.creatorEmail || this.creatorEmail;
    this.adminWidgetLoginUrl = this.resolveAdminWidgetLoginUrl(config);

    this.widgetPosition = config.position ?? 'bottom-right';
    this.displayStyle = config.displayStyle ?? 'text';
    this.displayBehavior = config.displayBehavior ?? 'always-visible';
    this.accentColor = this.sanitizeAccentColor(config.accentColor);
    this.launcherLabel = (config.launcherLabel ?? '').trim();
    this.logoUrl = (config.logoUrl ?? '').trim();
    this.launcherInitials = (config.launcherInitials ?? '').trim().slice(0, 2).toUpperCase();

    // Fetch runtime config from API to pick up admin-saved settings
    await this.fetchAndApplyWidgetConfig(config);

    this.render(this.selectedTheme, this.widgetPosition);
    this.bindEvents();
    this.bindBehaviorEvents();
    if (this.followAppTheme) {
      this.setupAppThemeObserver();
    }
    await this.restoreAdminSessionIfAvailable();
    await this.loadCategories(this.fallbackCategories);
  }

  private async restoreAdminSessionIfAvailable(): Promise<void> {
    if (!this.api || !this.api.hasStoredAdminSession()) return;
    try {
      const session = await this.api.getAdminSession();
      if (session.email) {
        this.adminEmail = session.email;
        const input = this.shadowRootRef.getElementById(
          'adminEmailInput'
        ) as HTMLInputElement | null;
        if (input) input.value = session.email;
      }
      const verify = await this.api.adminVerify();
      this.adminActionToken = verify.token;
      this.adminSessionActive = true;
      this.setAdminSessionUI(true);
      const adminStatus = this.shadowRootRef.getElementById('adminStatus');
      if (adminStatus) adminStatus.textContent = 'Session restored.';
    } catch {
      this.api.clearAdminSession();
      this.adminActionToken = undefined;
      this.adminSessionActive = false;
      this.setAdminSessionUI(false);
    }
  }

  disconnectedCallback(): void {
    this.stopRequestsPolling();
    this.teardownSyncChannel();
    this.teardownAppThemeObserver();
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = undefined;
    }
  }

  private setupSyncChannel(tenantKey: string): void {
    if (typeof BroadcastChannel === 'undefined') return;
    this.syncChannel = new BroadcastChannel(`faas-widget-sync-${tenantKey}`);
    this.syncChannel.onmessage = (event: MessageEvent<WidgetSyncMessage>) => {
      const message = event.data;
      if (!message) return;
      if (
        message.type === 'request-created' ||
        message.type === 'request-updated' ||
        message.type === 'requests-refresh'
      ) {
        if (!this.isDashboardOpen()) return;
        void this.refreshRequestsInBackground();
      }
    };
  }

  private teardownSyncChannel(): void {
    if (!this.syncChannel) return;
    this.syncChannel.close();
    this.syncChannel = undefined;
  }

  private publishSyncMessage(message: WidgetSyncMessage): void {
    if (!this.syncChannel) return;
    this.syncChannel.postMessage(message);
  }

  private loadSeenRequestUpdates(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage || !this.seenUpdatesStorageKey)
        return;
      const raw = window.localStorage.getItem(this.seenUpdatesStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, string>;
      if (parsed && typeof parsed === 'object') {
        this.seenRequestUpdates = parsed;
        this.seenUpdatesInitialized = Object.keys(parsed).length > 0;
      }
    } catch {
      this.seenRequestUpdates = {};
    }
  }

  private saveSeenRequestUpdates(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage || !this.seenUpdatesStorageKey)
        return;
      window.localStorage.setItem(
        this.seenUpdatesStorageKey,
        JSON.stringify(this.seenRequestUpdates)
      );
    } catch {
      // Ignore storage errors (private mode/quota/security settings).
    }
  }

  private initializeSeenRequestUpdatesBaseline(): void {
    if (this.seenUpdatesInitialized) return;
    this.requests.forEach((item) => {
      this.seenRequestUpdates[item.id] =
        item.updated_at || item.created_at || new Date().toISOString();
    });
    this.seenUpdatesInitialized = true;
    this.saveSeenRequestUpdates();
  }

  private markRequestAsSeen(requestId: string, seenAt?: string | null): void {
    if (!requestId) return;
    this.seenRequestUpdates[requestId] = seenAt || new Date().toISOString();
    this.seenUpdatesInitialized = true;
    this.saveSeenRequestUpdates();
  }

  private hasRequestNewUpdate(item: WidgetRequestItem): boolean {
    const lastSeen = this.seenRequestUpdates[item.id];
    // If we've never seen this item before and baseline is initialized, it's a new request
    if (!lastSeen) return this.seenUpdatesInitialized;
    const latest = item.updated_at || item.created_at;
    if (!latest) return false;
    const latestMs = Date.parse(latest);
    const seenMs = Date.parse(lastSeen);
    if (Number.isNaN(latestMs) || Number.isNaN(seenMs)) return false;
    return latestMs > seenMs;
  }

  private getNewUpdatesCount(): number {
    return this.requests.reduce(
      (count, item) => (this.hasRequestNewUpdate(item) ? count + 1 : count),
      0
    );
  }

  private readConfig(): WidgetConfig {
    return {
      apiUrl: this.getAttribute('api-url') || 'http://localhost:8000/api/v1',
      tenantKey: this.getAttribute('tenant-key') || 'local-tenant',
      authToken: this.getAttribute('auth-token') || undefined,
      userId: this.getAttribute('user-id') || undefined,
      userEmail: this.getAttribute('user-email') || undefined,
      userRole: (this.getAttribute('user-role') as 'user' | 'admin' | 'system') || undefined,
      adminEmail: this.getAttribute('admin-email') || undefined,
      creatorEmail: this.getAttribute('creator-email') || undefined,
      adminLoginUrl: this.getAttribute('admin-login-url') || undefined,
      appName: this.getAttribute('app-name') || undefined,
      accentColor: this.getAttribute('accent-color') || undefined,
      launcherLabel: this.getAttribute('launcher-label') || undefined,
      logoUrl: this.getAttribute('logo-url') || undefined,
      launcherInitials: this.getAttribute('launcher-initials') || undefined,
      theme: (this.getAttribute('theme') as WidgetTheme) || 'auto',
      position: (this.getAttribute('position') as WidgetPosition) || 'bottom-right',
      displayStyle: (this.getAttribute('display-style') as WidgetDisplayStyle) || 'text',
      displayBehavior:
        (this.getAttribute('display-behavior') as WidgetDisplayBehavior) || 'always-visible',
    };
  }

  /** Validate an accent color so only safe `#rgb` / `#rrggbb` values reach CSS. */
  private sanitizeAccentColor(value: string | undefined): string {
    const candidate = (value ?? '').trim();
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(candidate) ? candidate : '';
  }

  /** Darken a hex color by a factor (0-1) to derive a hover shade. */
  private shadeColor(hex: string, factor: number): string {
    let h = hex.replace('#', '');
    if (h.length === 3)
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    const num = parseInt(h, 16);
    const r = Math.max(0, Math.min(255, Math.round(((num >> 16) & 0xff) * (1 - factor))));
    const g = Math.max(0, Math.min(255, Math.round(((num >> 8) & 0xff) * (1 - factor))));
    const b = Math.max(0, Math.min(255, Math.round((num & 0xff) * (1 - factor))));
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  }

  /**
   * Resolve the URL of the dedicated Admin Widget Login page (the
   * `/widget-admin-login` route of the admin portal). Prefers an explicit
   * `admin-login-url` attribute set by the host/integration snippet; otherwise
   * falls back to the API origin (correct when the portal and API share a host,
   * e.g. production). The integration tenant key is appended so the login page
   * can pre-fill it. Returns an empty string when no valid URL can be built.
   */
  private resolveAdminWidgetLoginUrl(config: WidgetConfig): string {
    const tenant = config.tenantKey?.trim() || '';
    this.adminWidgetLoginTenantKey = tenant;
    // Note: tenant/email are intentionally NOT baked into the href so that a
    // direct/manual open of the URL carries no context. The tenant and admin
    // email are appended only when the user clicks the link (see the click
    // handler for #adminWidgetLoginLink).
    const explicit = config.adminLoginUrl?.trim();
    if (explicit) {
      try {
        const url = new URL(explicit, window.location.origin);
        if (!/\/widget-admin-login\/?$/.test(url.pathname)) {
          url.pathname = '/widget-admin-login';
        }
        return url.toString();
      } catch {
        return '';
      }
    }
    // Build-time override: point at the admin portal origin explicitly. Useful
    // when the portal and API do not share a host (any environment).
    const envOrigin = import.meta.env.VITE_ADMIN_PORTAL_ORIGIN?.trim();
    if (envOrigin) {
      try {
        return new URL('/widget-admin-login', envOrigin).toString();
      } catch {
        // fall through to API-origin derivation
      }
    }
    try {
      const api = new URL(config.apiUrl, window.location.origin);
      // Local dev: the backend API runs on :8000, but the admin portal SPA
      // that serves the /widget-admin-login route runs on :5175. Retarget so
      // the link resolves to a real page instead of a backend 404. In
      // production the API and portal share an origin, so this is a no-op.
      const isLocalHost = api.hostname === 'localhost' || api.hostname === '127.0.0.1';
      if (isLocalHost && api.port === '8000') {
        api.port = '5175';
      }
      return new URL('/widget-admin-login', api.origin).toString();
    } catch {
      return '';
    }
  }

  // Builds the admin-widget-login URL carrying tenant + admin email in the URL
  // hash (never sent to the server, and stripped by the login page on load) so
  // the context isn't exposed as query params. Called only on click.
  private buildAdminWidgetLoginClickUrl(): string {
    if (!this.adminWidgetLoginUrl) return '';
    try {
      const url = new URL(this.adminWidgetLoginUrl, window.location.origin);
      const emailInput = this.shadowRootRef.getElementById(
        'adminEmailInput'
      ) as HTMLInputElement | null;
      const email = (emailInput?.value || this.adminEmail || '').trim();
      const hint = new URLSearchParams();
      if (this.adminWidgetLoginTenantKey) hint.set('tenant', this.adminWidgetLoginTenantKey);
      if (email) hint.set('email', email);
      // Clear any pre-existing query hints so nothing leaks into the address bar.
      url.searchParams.delete('tenant');
      url.searchParams.delete('email');
      const hintStr = hint.toString();
      url.hash = hintStr ? hintStr : '';
      return url.toString();
    } catch {
      return this.adminWidgetLoginUrl;
    }
  }

  private async fetchAndApplyWidgetConfig(config: WidgetConfig): Promise<void> {
    try {
      const apiUrl = config.apiUrl || this.getAttribute('api-url') || '';
      const tenantKey = config.tenantKey || this.getAttribute('tenant-key') || '';
      const authToken = config.authToken || this.getAttribute('auth-token') || '';
      if (!apiUrl || !tenantKey) return;

      const headers: Record<string, string> = { 'X-Tenant-Key': tenantKey };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      const resp = await fetch(`${apiUrl}/integrations/widget-config`, { headers });
      if (!resp.ok) return;

      const data = await resp.json();
      if (data.position) this.widgetPosition = data.position as WidgetPosition;
      if (data.display_style) this.displayStyle = data.display_style as WidgetDisplayStyle;
      if (data.display_behavior)
        this.displayBehavior = data.display_behavior as WidgetDisplayBehavior;
      if (data.accent_color) this.accentColor = this.sanitizeAccentColor(String(data.accent_color));
      if (data.launcher_label) this.launcherLabel = String(data.launcher_label).trim();
      if (data.logo_url) this.logoUrl = String(data.logo_url).trim();
      if (data.launcher_initials)
        this.launcherInitials = String(data.launcher_initials).trim().slice(0, 2).toUpperCase();
    } catch {
      // Silently fail — use HTML attribute defaults
    }
  }

  private buildSeenUpdatesStorageKey(
    tenantKey: string,
    userId?: string,
    userEmail?: string
  ): string {
    const userPart = userId?.trim() || userEmail?.trim() || 'anonymous';
    return `faas_seen_request_updates:${tenantKey}:${userPart.toLowerCase()}`;
  }

  private readUserContextFromAttributes(): {
    userId?: string;
    userEmail?: string;
    userRole?: 'user' | 'admin' | 'system';
  } {
    const userId = this.getAttribute('user-id') || undefined;
    const userEmail = this.getAttribute('user-email') || undefined;
    const roleAttr = this.getAttribute('user-role');
    const userRole =
      roleAttr === 'user' || roleAttr === 'admin' || roleAttr === 'system' ? roleAttr : undefined;
    return { userId, userEmail, userRole };
  }

  private applyUserContextFromAttributes(refreshRequests = false): void {
    if (!this.api) return;

    const context = this.readUserContextFromAttributes();
    this.api.setUserContext({
      ...context,
      userRole: context.userRole || 'user',
    });

    const tenantKey = this.getAttribute('tenant-key') || 'local-tenant';
    const nextSeenKey = this.buildSeenUpdatesStorageKey(
      tenantKey,
      context.userId,
      context.userEmail
    );
    if (nextSeenKey !== this.seenUpdatesStorageKey) {
      this.seenUpdatesStorageKey = nextSeenKey;
      this.seenRequestUpdates = {};
      this.seenUpdatesInitialized = false;
      this.loadSeenRequestUpdates();
    }

    if (!refreshRequests) return;
    const requestsView = this.shadowRootRef.getElementById('requestsView');
    if (requestsView && !requestsView.classList.contains('hidden')) {
      void this.loadRequestsView();
    }
  }

  private readCaptureMode(): CaptureMode {
    const mode = (this.getAttribute('capture-mode') || 'customized').toLowerCase();
    if (
      mode === 'old-basic' ||
      mode === 'full' ||
      mode === 'customized' ||
      mode === 'snippet' ||
      mode === 'upload'
    ) {
      return mode;
    }
    return 'customized';
  }

  private render(theme: 'light' | 'dark', position: WidgetPosition): void {
    const resolvedTheme = theme;
    const behaviorClass =
      this.displayBehavior !== 'always-visible' ? ` ${this.displayBehavior}` : '';
    const styleClass = this.displayStyle !== 'text' ? ` display-${this.displayStyle}` : '';
    const draggableAttr =
      this.displayBehavior === 'draggable' || this.displayBehavior === 'draggable-hide'
        ? ' draggable-widget'
        : '';

    const defaultIcon =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const label = this.escapeHtml(this.launcherLabel || 'Feedback');
    const logoImg = this.logoUrl
      ? `<img class="fab-logo" src="${this.escapeHtml(this.logoUrl)}" alt="" />`
      : '';
    const initialsBadge = this.launcherInitials
      ? `<span class="fab-initials">${this.escapeHtml(this.launcherInitials)}</span>`
      : '';
    // Circle style: explicit logo > explicit initials badge > default chat icon.
    const circleContent = logoImg || initialsBadge || defaultIcon;
    // Inline icon (for text/tab-horizontal): logo > initials badge > chat glyph.
    const inlineIcon = this.logoUrl
      ? `<img class="fab-logo fab-logo-inline" src="${this.escapeHtml(this.logoUrl)}" alt="" />`
      : initialsBadge
        ? `<span class="fab-initials fab-initials-inline">${this.escapeHtml(this.launcherInitials)}</span>`
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle; margin-right: 6px;"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const fabContent =
      this.displayStyle === 'circle'
        ? circleContent
        : this.displayStyle === 'tab'
          ? `<span class="tab-label">${label}</span>`
          : this.displayStyle === 'tab-horizontal'
            ? `${this.logoUrl ? logoImg : initialsBadge || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle;"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'}<span style="margin-left:6px">${label}</span>`
            : `${inlineIcon}${label}`;

    const accentStyleAttr = this.accentColor
      ? ` style="--faas-primary: ${this.accentColor}; --faas-primary-hover: ${this.shadeColor(this.accentColor, 0.14)};"`
      : '';

    this.shadowRootRef.innerHTML = `
			<style>${widgetStyles}</style>

			<!-- ── Snip overlay (area selection) ── -->
			<div id="snipOverlay" class="snip-overlay hidden">
				<div class="snip-toolbar">
					<span class="snip-help">Drag to select area</span>
					<div class="snip-spacer"></div>
					<button id="snipCancel" class="snip-cancel">Cancel</button>
					<button id="snipConfirm" class="snip-confirm" disabled>Use selection</button>
				</div>
				<div class="snip-canvas-wrap" id="snipCanvasWrap">
					<canvas id="snipCanvas"></canvas>
					<div id="snipRect" class="snip-rect hidden"></div>
				</div>
			</div>

			<!-- ── Annotation overlay (full-screen, shown after capture) ── -->
			<div id="annotOverlay" class="annot-overlay hidden">
				<div class="annot-toolbar">
					<button id="annotPen"       class="annot-tool active" title="Pen">✏️ Pen</button>
					<button id="annotHighlight" class="annot-tool"        title="Highlight">🟡 Highlight</button>
					<button id="annotEraser"    class="annot-tool"        title="Eraser">⬜ Eraser</button>
					<div class="annot-sep"></div>
					<span class="annot-label">Color</span>
					<input type="color" id="annotColor" class="annot-color" value="#ef4444" title="Pen color">
					<span class="annot-label">Size</span>
					<input type="range" id="annotSize" class="annot-size" min="2" max="24" value="4" title="Brush size">
					<div class="annot-sep"></div>
					<button id="annotUndo" class="annot-tool" title="Undo (Ctrl+Z)">↩ Undo</button>
					<div class="annot-spacer"></div>
					<button id="annotRetake" class="annot-retake">↺ Retake</button>
					<button id="annotDone"   class="annot-done">✓ Done</button>
				</div>
				<div class="annot-canvas-wrap" id="annotCanvasWrap">
					<canvas id="annotBgCanvas"></canvas>
					<canvas id="annotDrawCanvas"></canvas>
				</div>
			</div>

			<!-- ── Widget FAB + panel ── -->
			<div class="faas-widget ${resolvedTheme} ${position}${behaviorClass}${styleClass}${draggableAttr}"${accentStyleAttr}>
				<button class="fab" id="fab" aria-label="Open feedback form">${fabContent}</button>
				<div class="panel ${this.isOpen ? 'open' : ''}" id="panel">
					<div class="panel-header">
						<h3>Share Feedback</h3>
						<div class="panel-header-actions">
							<div class="theme-toggle" role="group" aria-label="Theme">
								<button id="themeLightBtn" type="button" class="theme-toggle-btn icon-only ${this.selectedTheme === 'light' ? 'active' : ''}" aria-pressed="${this.selectedTheme === 'light'}" aria-label="Light theme" title="Light theme">
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
										<circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8"></circle>
										<path d="M12 2V5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M12 19V22" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M4.93 4.93L7.05 7.05" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M16.95 16.95L19.07 19.07" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M2 12H5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M19 12H22" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M4.93 19.07L7.05 16.95" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
										<path d="M16.95 7.05L19.07 4.93" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
									</svg>
								</button>
								<button id="themeDarkBtn" type="button" class="theme-toggle-btn icon-only ${this.selectedTheme === 'dark' ? 'active' : ''}" aria-pressed="${this.selectedTheme === 'dark'}" aria-label="Dark theme" title="Dark theme">
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
										<path d="M20 14.5C19.2 14.8 18.35 15 17.45 15C13.34 15 10 11.66 10 7.55C10 6.65 10.2 5.8 10.5 5C7 5.75 4.5 8.86 4.5 12.5C4.5 16.69 7.81 20 12 20C15.64 20 18.75 17.5 19.5 14C19.34 14.18 19.17 14.35 20 14.5Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
									</svg>
								</button>
							</div>
							<button id="closePanel" class="ghost" aria-label="Close">✕</button>
						</div>
					</div>
					<p class="panel-sub">Tell us what happened. Attaching a screenshot is optional.</p>

					<div class="actions-row">
						<button id="tabCapture" class="ghost active-tab">Capture</button>
						${this.canViewRequestsTab ? '<button id="tabRequests" class="ghost">Requests <span id="requestsCountBadge" class="tab-count-badge" style="display:none"></span></button>' : ''}
					</div>

					<div id="captureView">

					<div class="view-scroll">
					<label class="label" for="userEmailInput">Your email <span class="req-mark" aria-hidden="true">*</span></label>
					<input id="userEmailInput" type="email" class="input" placeholder="name@company.com" required aria-required="true">
					<p class="hint">Required so your requests can be matched by email.</p>

					<label class="label" for="feedbackText">Your feedback <span class="req-mark" aria-hidden="true">*</span></label>
					<textarea id="feedbackText" class="input" rows="3" placeholder="Describe your issue or idea..." required aria-required="true"></textarea>

					<div class="rating-block">
						<label class="label rating-label" id="ratingLabel">Rate your experience (optional)</label>
						<div id="ratingStars" class="rating-stars" role="radiogroup" aria-labelledby="ratingLabel">
							${[1, 2, 3, 4, 5]
                .map(
                  (value) => `
								<button type="button" class="rating-star ${this.selectedRating >= value ? 'active' : ''}" data-rating-star="${value}" role="radio" aria-checked="${this.selectedRating === value}" aria-label="${value} star${value > 1 ? 's' : ''}" title="${value} star${value > 1 ? 's' : ''}">★</button>
							`
                )
                .join('')}
						</div>
					</div>

					<label class="label" for="pageContextInput">Page</label>
					<input id="pageContextInput" type="text" class="input input-readonly" readonly tabindex="-1" title="The page this feedback is about" placeholder="Current page">

					<label class="label" for="categorySelect">Category</label>
					<select id="categorySelect" class="input">
						<option value="">General</option>
					</select>
					<input id="categoryOtherInput" type="text" class="input hidden" placeholder="Enter category" aria-label="Other category">

					<label class="label" for="captureMode">Screenshot mode</label>
					<select id="captureMode" class="input">
						<option value="customized" ${this.captureMode === 'customized' ? 'selected' : ''}>Smart annotate (recommended)</option>
						<option value="snippet" ${this.captureMode === 'snippet' ? 'selected' : ''}>Snippet + smart annotate</option>
						<option value="full" ${this.captureMode === 'full' ? 'selected' : ''}>Quick full capture (page only)</option>
						<option value="old-basic" ${this.captureMode === 'old-basic' ? 'selected' : ''}>Basic viewport capture (legacy)</option>
						<option value="upload" ${this.captureMode === 'upload' ? 'selected' : ''}>Upload image</option>
					</select>

					<div id="captureControls" class="${this.captureMode === 'upload' ? 'hidden' : ''}">
						<div class="actions-row">
							<button id="captureBtn" class="secondary">Capture screenshot</button>
						</div>
					</div>

					<div id="uploadDropZone" class="upload-drop-zone${this.captureMode === 'upload' ? '' : ' hidden'}">
						<input type="file" id="uploadFile" accept="image/png,image/jpeg,image/gif,image/webp" style="display:none">
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="upload-drop-icon" aria-hidden="true">
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
							<polyline points="17 8 12 3 7 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
							<line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
						</svg>
						<p class="upload-drop-title"><strong>Click to upload</strong> or drag and drop</p>
						<p class="upload-drop-hint">PNG, JPG, GIF or WebP (max. 5 MB)</p>
					</div>

					<span id="captureState" class="hint">No screenshot attached</span>
					</div>

					<div class="view-footer">
					<div class="actions-row end">
						<button id="submitBtn" class="primary btn-with-icon">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
								<path d="M5 12.5L10 17.5L19 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
							</svg>
							<span>Submit</span>
						</button>
					</div>
					<p id="statusMsg" class="status"></p>
					</div>
					</div>

					${
            this.canViewRequestsTab
              ? `
					<div id="requestsView" class="hidden">
						<div class="view-scroll">
						<div id="adminLoginSection">
							<label class="label" for="adminEmailInput">Admin Email</label>
							<input id="adminEmailInput" type="email" class="input" value="${this.adminEmail}">
							<label class="label" for="adminPasswordInput">Password</label>
							<input id="adminPasswordInput" type="password" class="input" placeholder="Enter password">
							<div class="actions-row">
								<button id="adminLoginBtn" class="secondary">Admin Login</button>
							</div>
							${
                this.adminWidgetLoginUrl
                  ? `
							<p class="hint admin-login-link-hint">
								Prefer the full login page?
								<a id="adminWidgetLoginLink" class="admin-login-link" href="${this.adminWidgetLoginUrl}" target="_blank" rel="noopener noreferrer">Go to Admin Widget Login page</a>
							</p>`
                  : ''
              }
						</div>
						<div id="dashboardActions">
							<div class="actions-row actions-row-between">
								<button id="openDashboardBtn" class="primary">Open Requests</button>
								<button id="adminLogoutBtn" class="ghost icon-btn hidden" aria-label="Logout" title="Logout">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
										<path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"></path>
									</svg>
								</button>
							</div>
						</div>
						<p id="adminStatus" class="hint">Open Requests to view your submissions. Admin login is only required for updates.</p>
						</div>

						<div id="dashboardDialog" class="dashboard-dialog hidden" aria-hidden="true">
							<div id="dashboardBackdrop" class="dashboard-backdrop"></div>
							<div class="dashboard-card" role="dialog" aria-modal="true" aria-label="Widget dashboard">
								<div class="dashboard-header">
									<h4>Request Dashboard</h4>
									<div class="dashboard-tabs" role="tablist" aria-label="Dashboard view tabs">
										<button id="dashboardTabTable" class="dash-tab active" type="button" role="tab" aria-selected="true">Table</button>
										<button id="dashboardTabDetail" class="dash-tab" type="button" role="tab" aria-selected="false">Detail</button>
									</div>
									<button id="closeDashboardBtn" class="ghost" aria-label="Close dashboard">✕</button>
								</div>
								<div id="dashboardBody" class="dashboard-body mode-table">
									<div id="dashboardTablePane" class="dashboard-left-pane">
										<div class="requests-toolbar">
											<input id="requestsSearchInput" class="input requests-search" type="search" placeholder="Search by title or request id" />
											<div id="requestsStatusChips" class="status-filters" role="group" aria-label="Filter requests by status">
												<button type="button" class="chip active" data-status="all">All</button>
												<button type="button" class="chip" data-status="updated">New updates</button>
												<button type="button" class="chip" data-status="submitted">Submitted</button>
												<button type="button" class="chip" data-status="in_review">In review</button>
												<button type="button" class="chip" data-status="needs_info">Needs info</button>
												<button type="button" class="chip" data-status="approved">Approved</button>
												<button type="button" class="chip" data-status="rejected">Rejected</button>
											</div>
											<div class="requests-filter-row">
												<div id="requestsUserFilterSummary" class="filter-summary" aria-live="polite"></div>
												<div class="requests-toolbar-actions">
													<button id="openExportBtn" type="button" class="ghost filter-trigger" title="Export all requests" aria-haspopup="dialog">
														<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
															<path d="M12 3V15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
															<path d="M8 11L12 15L16 11" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
															<path d="M5 19H19" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
														</svg>
														Export all
													</button>
													<button id="openRequesterFilterBtn" type="button" class="ghost filter-trigger admin-only-filter hidden" aria-haspopup="dialog" aria-expanded="false" aria-controls="requestsUserFilterPanel">
														<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
															<path d="M4 6H20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
															<path d="M7 12H17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
															<path d="M10 18H14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
														</svg>
														Filter
													</button>
												</div>
											</div>
											<div id="requestsUserFilterBackdrop" class="user-filter-backdrop hidden"></div>
											<div id="requestsUserFilterPanel" class="user-filter-panel hidden" role="dialog" aria-modal="true" aria-label="Filters">
												<div class="user-filter-head">
													<h5>Filters</h5>
													<div class="user-filter-head-actions">
														<button id="clearRequesterFilterBtn" type="button" class="ghost filter-clear-btn">Clear</button>
														<button id="closeRequesterFilterBtn" type="button" class="ghost filter-close-btn" aria-label="Close">✕</button>
													</div>
												</div>
												<div class="user-filter-scroll-body">
													<p class="hint user-filter-subtitle">Refine your request list.</p>
													<div class="user-filter-search-wrap">
														<svg class="user-filter-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8"/><path d="M16.5 16.5L21 21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
														<input id="requesterSearchInput" class="input user-filter-search-input" type="search" placeholder="Search user" />
													</div>
													<div class="filter-section">
														<button type="button" class="filter-section-head" id="filterUserSectionToggle" aria-expanded="true"><span class="filter-section-title">User</span><svg class="filter-section-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
														<div id="requestsUserFilterList" class="user-filter-list filter-section-body" role="listbox" aria-multiselectable="true" aria-label="User filters"></div>
													</div>
													<div class="filter-section">
														<button type="button" class="filter-section-head" id="filterStatusSectionToggle" aria-expanded="true"><span class="filter-section-title">Status</span><svg class="filter-section-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
														<div id="requestsStatusFilterList" class="user-filter-list filter-section-body" role="listbox" aria-multiselectable="true" aria-label="Status filters"></div>
													</div>
												</div>
												<div class="user-filter-footer">
													<span id="requestsUserFilterMeta" class="user-filter-selected-count">Selected 0 items</span>
													<div class="user-filter-footer-actions">
														<button id="closeRequesterFilterBtn2" type="button" class="ghost">Cancel</button>
														<button id="applyRequesterFilterBtn" type="button" class="primary">Apply</button>
													</div>
												</div>
											</div>
										</div>
										<div id="requestsList" class="requests-list"></div>
										<div id="requestsPager" class="requests-pager"></div>
									</div>
									<div id="requestDetail" class="request-detail hidden"></div>
								</div>
							</div>
						</div>
						<div id="deleteConfirmDialog" class="confirm-dialog hidden" aria-hidden="true">
							<div id="deleteConfirmBackdrop" class="confirm-backdrop"></div>
							<div class="confirm-card" role="dialog" aria-modal="true" aria-labelledby="deleteConfirmTitle">
								<div class="confirm-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M9 3H15L16 5H21V7H3V5H8L9 3Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>
										<path d="M6 7H18L17 19C16.92 20.1 16.01 21 14.9 21H9.1C7.99 21 7.08 20.1 7 19L6 7Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>
										<path d="M10 11V17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
										<path d="M14 11V17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
									</svg>
								</div>
								<h5 id="deleteConfirmTitle">Delete this request?</h5>
								<p id="deleteConfirmMessage" class="confirm-message">This request will be removed from both dashboard views.</p>
								<div class="confirm-actions">
									<button id="deleteCancelBtn" type="button" class="ghost">Cancel</button>
									<button id="deleteConfirmBtn" type="button" class="ghost danger">Delete</button>
								</div>
							</div>
						</div>
						<div id="exportDialog" class="confirm-dialog export-dialog hidden" aria-hidden="true">
							<div id="exportBackdrop" class="confirm-backdrop"></div>
							<div class="confirm-card export-card" role="dialog" aria-modal="true" aria-labelledby="exportDialogTitle">
								<div class="export-head">
									<h5 id="exportDialogTitle">Export requests</h5>
									<button id="exportCloseBtn" type="button" class="ghost icon-btn" aria-label="Close export">✕</button>
								</div>
								<p class="confirm-message">Download all requests matching the filters below.</p>
								<div class="export-field">
									<span class="export-label">Format</span>
									<div class="export-format-options">
										<label class="export-radio"><input type="radio" name="exportFormat" value="excel" checked /> Excel</label>
										<label class="export-radio"><input type="radio" name="exportFormat" value="pdf" /> PDF</label>
									</div>
								</div>
								<div class="export-field export-date-row">
									<label class="export-date"><span class="export-label">From date</span><input id="exportFromDate" class="input" type="date" /></label>
									<label class="export-date"><span class="export-label">To date</span><input id="exportToDate" class="input" type="date" /></label>
								</div>
								<div class="export-field">
									<span class="export-label">Status</span>
									<div id="exportStatusList" class="export-status-list">
										<label class="export-check"><input type="checkbox" data-export-status value="all" checked /> All statuses</label>
										<label class="export-check"><input type="checkbox" data-export-status value="submitted" /> Submitted</label>
										<label class="export-check"><input type="checkbox" data-export-status value="in_review" /> In review</label>
										<label class="export-check"><input type="checkbox" data-export-status value="needs_info" /> Needs info</label>
										<label class="export-check"><input type="checkbox" data-export-status value="approved" /> Approved</label>
										<label class="export-check"><input type="checkbox" data-export-status value="rejected" /> Rejected</label>
									</div>
								</div>
								<div class="export-field">
									<label class="export-check"><input id="exportIncludeImages" type="checkbox" checked /> Include images (small thumbnail, click to open full size)</label>
								</div>
								<p id="exportStatusMessage" class="confirm-message export-status-message" aria-live="polite"></p>
								<div class="confirm-actions">
									<button id="exportCancelBtn" type="button" class="ghost">Cancel</button>
									<button id="exportRunBtn" type="button" class="primary">Download</button>
								</div>
							</div>
						</div>
					</div>
					`
              : ''
          }
				</div>
				<div id="widgetToast" class="widget-toast" role="status" aria-live="polite" aria-atomic="true"></div>
			</div>
		`;
  }

  private bindBehaviorEvents(): void {
    const widget = this.shadowRootRef.querySelector('.faas-widget') as HTMLElement | null;
    const fab = this.shadowRootRef.getElementById('fab') as HTMLElement | null;
    if (!widget || !fab) return;

    // Half-hidden behavior: start peeking, expand on hover/click
    if (this.displayBehavior === 'half-hidden') {
      widget.classList.add('half-hidden-state');
      widget.addEventListener('mouseenter', () => widget.classList.remove('half-hidden-state'));
      widget.addEventListener('mouseleave', () => {
        if (!this.isOpen) widget.classList.add('half-hidden-state');
      });
      // When panel opens (FAB click), fully reveal
      fab.addEventListener('click', () => widget.classList.remove('half-hidden-state'));
    }

    // Draggable behavior
    if (this.displayBehavior === 'draggable' || this.displayBehavior === 'draggable-hide') {
      const panelHeader = this.shadowRootRef.querySelector('.panel-header') as HTMLElement | null;

      const startDrag = (e: MouseEvent) => {
        this.isDragging = true;
        const rect = widget.getBoundingClientRect();
        this.dragOffsetX = e.clientX - rect.left;
        this.dragOffsetY = e.clientY - rect.top;
        widget.style.cursor = 'grabbing';
        e.preventDefault();
      };

      fab.style.cursor = 'grab';
      fab.addEventListener('mousedown', startDrag);

      if (panelHeader) {
        panelHeader.style.cursor = 'grab';
        panelHeader.addEventListener('mousedown', startDrag);
      }

      document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!this.isDragging) return;
        widget.style.position = 'fixed';
        widget.style.left = `${e.clientX - this.dragOffsetX}px`;
        widget.style.top = `${e.clientY - this.dragOffsetY}px`;
        widget.style.right = 'auto';
        widget.style.bottom = 'auto';
      });
      document.addEventListener('mouseup', () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        widget.style.cursor = '';
        // Auto-hide after drag for draggable-hide
        if (this.displayBehavior === 'draggable-hide') {
          this.startHideTimer(widget);
        }
      });
    }

    // Auto-hide for draggable-hide: hide after inactivity
    if (this.displayBehavior === 'draggable-hide') {
      this.startHideTimer(widget);
      widget.addEventListener('mouseenter', () => {
        window.clearTimeout(this.hideTimer);
        widget.classList.remove('auto-hidden');
      });
      widget.addEventListener('mouseleave', () => {
        if (!this.isOpen) this.startHideTimer(widget);
      });
    }
  }

  private startHideTimer(widget: HTMLElement): void {
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      if (!this.isOpen) widget.classList.add('auto-hidden');
    }, 3000);
  }

  private bindEvents(): void {
    this.shadowRootRef.getElementById('fab')?.addEventListener('click', () => {
      this.isOpen = !this.isOpen;
      this.syncOpenState();
      if (this.isOpen) {
        void this.api?.trackEvent('widget_panel', 'opened');
        this.refreshPageContextField();
        void this.loadCategories(this.fallbackCategories);
      }
    });

    this.shadowRootRef.getElementById('closePanel')?.addEventListener('click', () => {
      this.isOpen = false;
      this.syncOpenState();
    });

    this.shadowRootRef.getElementById('themeLightBtn')?.addEventListener('click', () => {
      this.setTheme('light');
    });

    this.shadowRootRef.getElementById('themeDarkBtn')?.addEventListener('click', () => {
      this.setTheme('dark');
    });

    this.shadowRootRef.getElementById('captureBtn')?.addEventListener('click', async () => {
      void this.api?.trackEvent('screenshot_capture', 'click', { capture_mode: this.captureMode });
      await this.captureScreenshot();
    });

    // Upload drop zone
    const uploadDropZone = this.shadowRootRef.getElementById('uploadDropZone');
    uploadDropZone?.addEventListener('click', () => {
      (this.shadowRootRef.getElementById('uploadFile') as HTMLInputElement | null)?.click();
    });
    uploadDropZone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadDropZone.classList.add('drag-over');
    });
    uploadDropZone?.addEventListener('dragleave', (e) => {
      if (!uploadDropZone.contains(e.relatedTarget as Node)) {
        uploadDropZone.classList.remove('drag-over');
      }
    });
    uploadDropZone?.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadDropZone.classList.remove('drag-over');
      const file = (e as DragEvent).dataTransfer?.files[0];
      if (file) this.handleUploadedFile(file);
    });

    this.shadowRootRef.getElementById('uploadFile')?.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) this.handleUploadedFile(file);
    });

    this.shadowRootRef.getElementById('captureMode')?.addEventListener('change', (e) => {
      const mode = (e.target as HTMLSelectElement).value as CaptureMode;
      this.captureMode = mode;
      const captureControls = this.shadowRootRef.getElementById('captureControls');
      const dropZone = this.shadowRootRef.getElementById('uploadDropZone');
      const captureState = this.shadowRootRef.getElementById('captureState');
      if (mode === 'upload') {
        captureControls?.classList.add('hidden');
        dropZone?.classList.remove('hidden');
      } else {
        captureControls?.classList.remove('hidden');
        dropZone?.classList.add('hidden');
        // clear any uploaded file when switching away
        this.screenshotBlob = undefined;
        if (captureState) captureState.textContent = 'No screenshot attached';
        const uploadFile = this.shadowRootRef.getElementById(
          'uploadFile'
        ) as HTMLInputElement | null;
        if (uploadFile) uploadFile.value = '';
      }
    });

    this.shadowRootRef.getElementById('categorySelect')?.addEventListener('change', () => {
      this.updateCategoryOtherVisibility();
    });

    const userEmailInput = this.shadowRootRef.getElementById(
      'userEmailInput'
    ) as HTMLInputElement | null;
    if (userEmailInput) {
      const initialEmail = (this.getAttribute('user-email') || '').trim();
      if (initialEmail) userEmailInput.value = initialEmail;
      userEmailInput.addEventListener('change', () => {
        const normalized = userEmailInput.value
          .trim()
          .replace(/[;,]+$/g, '')
          .toLowerCase();
        userEmailInput.value = normalized;
        this.applyOptionalUserEmailContext();
      });
    }

    this.refreshPageContextField();
    this.bindRatingEvents();
    this.updateRatingUI();

    this.shadowRootRef.getElementById('submitBtn')?.addEventListener('click', async () => {
      await this.submit();
    });

    this.shadowRootRef.getElementById('tabCapture')?.addEventListener('click', () => {
      this.switchTab('capture');
    });

    this.shadowRootRef.getElementById('tabRequests')?.addEventListener('click', () => {
      this.switchTab('requests');
      void this.loadRequestsView();
      if (!this.adminSessionActive) {
        const adminStatus = this.shadowRootRef.getElementById('adminStatus');
        if (adminStatus)
          adminStatus.textContent =
            'Read-only mode. You can check request status here. Admin login is required for updates.';
      }
    });

    this.shadowRootRef.getElementById('adminLoginBtn')?.addEventListener('click', async () => {
      const adminPasswordInput = this.shadowRootRef.getElementById(
        'adminPasswordInput'
      ) as HTMLInputElement | null;
      const adminEmailInput = this.shadowRootRef.getElementById(
        'adminEmailInput'
      ) as HTMLInputElement | null;
      const adminStatus = this.shadowRootRef.getElementById('adminStatus');
      if (!adminPasswordInput || !adminEmailInput || !adminStatus || !this.api) return;
      const normalizedAdminEmail = adminEmailInput.value
        .trim()
        .replace(/[;,]+$/g, '')
        .toLowerCase();
      if (normalizedAdminEmail) {
        this.adminEmail = normalizedAdminEmail;
        adminEmailInput.value = normalizedAdminEmail;
      }
      this.adminPassword = adminPasswordInput.value;
      try {
        await this.ensureVerifiedAdminSession();
        this.setAdminSessionUI(true);
        this.startRequestsPolling();
        adminStatus.textContent = 'Login successful. Open Dashboard to manage requests.';
        this.showToast('Admin login successful.', 'success');
        await this.loadRequestsView();
        this.openDashboardDialog();
      } catch (error) {
        const errorMessage = this.normalizeUiErrorMessage(error);
        this.setAdminSessionUI(false);
        adminStatus.textContent = `Admin login failed: ${errorMessage}`;
        this.showToast(`Admin login failed: ${errorMessage}`, 'error');
      }
    });

    this.shadowRootRef.getElementById('openDashboardBtn')?.addEventListener('click', () => {
      this.openDashboardDialog();
    });

    this.shadowRootRef
      .getElementById('adminWidgetLoginLink')
      ?.addEventListener('click', (event) => {
        event.preventDefault();
        const url = this.buildAdminWidgetLoginClickUrl();
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      });

    this.shadowRootRef.getElementById('openExportBtn')?.addEventListener('click', () => {
      this.openExportDialog();
    });

    this.shadowRootRef.getElementById('exportCloseBtn')?.addEventListener('click', () => {
      this.closeExportDialog();
    });

    this.shadowRootRef.getElementById('exportCancelBtn')?.addEventListener('click', () => {
      this.closeExportDialog();
    });

    this.shadowRootRef.getElementById('exportBackdrop')?.addEventListener('click', () => {
      this.closeExportDialog();
    });

    this.shadowRootRef.getElementById('exportStatusList')?.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement | null;
      if (!target || target.getAttribute('data-export-status') === null) return;
      this.onExportStatusToggle(target);
    });

    this.shadowRootRef.getElementById('exportRunBtn')?.addEventListener('click', () => {
      void this.runBulkExport();
    });

    this.shadowRootRef.getElementById('dashboardTabTable')?.addEventListener('click', () => {
      this.setDashboardTab('table');
    });

    this.shadowRootRef.getElementById('dashboardTabDetail')?.addEventListener('click', () => {
      this.setDashboardTab('detail');
    });

    this.shadowRootRef.getElementById('adminLogoutBtn')?.addEventListener('click', () => {
      this.logoutAdmin();
    });

    this.shadowRootRef.getElementById('closeDashboardBtn')?.addEventListener('click', () => {
      this.closeDashboardDialog();
    });

    this.shadowRootRef.getElementById('dashboardBackdrop')?.addEventListener('click', () => {
      this.closeDashboardDialog();
    });

    this.shadowRootRef.getElementById('deleteCancelBtn')?.addEventListener('click', () => {
      this.resolveDeleteConfirmation(false);
    });

    this.shadowRootRef.getElementById('deleteConfirmBtn')?.addEventListener('click', () => {
      this.resolveDeleteConfirmation(true);
    });

    this.shadowRootRef.getElementById('deleteConfirmBackdrop')?.addEventListener('click', () => {
      this.resolveDeleteConfirmation(false);
    });

    this.shadowRootRef.getElementById('requestsList')?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-status-select-request-id]')) return;

      const deleteButton = target?.closest('[data-delete-request-id]') as HTMLElement | null;
      if (deleteButton) {
        if (!this.adminSessionActive) {
          const adminStatus = this.shadowRootRef.getElementById('adminStatus');
          if (adminStatus) adminStatus.textContent = 'Admin login required for delete action.';
          this.showToast('Admin login required for delete action.', 'error');
          return;
        }
        const requestIdToDelete = deleteButton.getAttribute('data-delete-request-id');
        if (requestIdToDelete) {
          void this.deleteRequestById(requestIdToDelete);
        }
        return;
      }

      const row = target?.closest('[data-request-id]') as HTMLElement | null;
      const requestId = row?.getAttribute('data-request-id');
      if (!requestId) return;
      void this.loadRequestDetail(requestId);
      this.setDashboardTab('detail');
    });

    this.shadowRootRef.getElementById('requestsList')?.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement | null;
      if (!target || !target.matches('[data-status-select-request-id]')) return;
      const requestId = target.getAttribute('data-status-select-request-id');
      const nextStatus = target.value as WidgetRequestItem['status'];
      if (!requestId) return;
      void this.updateStatusFromTable(requestId, nextStatus);
    });

    this.shadowRootRef.getElementById('requestsSearchInput')?.addEventListener('input', (event) => {
      const input = event.target as HTMLInputElement;
      this.requestSearchQuery = input.value.trim().toLowerCase();
      this.requestPage = 1;
      this.ensureVisibleSelection();
      this.renderRequestList();
    });

    this.shadowRootRef.getElementById('requestsStatusChips')?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const chip = target?.closest('[data-status]') as HTMLButtonElement | null;
      if (!chip) return;
      const status = chip.dataset.status as
        'all' | 'updated' | WidgetRequestItem['status'] | undefined;
      if (!status) return;
      this.requestStatusFilter = status;
      this.requestStatusFilters = [status];
      this.requestPage = 1;
      this.ensureVisibleSelection();
      this.renderStatusChips();
      this.renderRequesterChips();
      this.renderRequestList();
    });

    this.shadowRootRef.getElementById('openRequesterFilterBtn')?.addEventListener('click', () => {
      if (!this.adminSessionActive) return;
      if (this.requestUserFilterPanelOpen) {
        this.closeRequesterFilterPanel();
        return;
      }
      this.openRequesterFilterPanel();
    });

    this.shadowRootRef.getElementById('closeRequesterFilterBtn')?.addEventListener('click', () => {
      this.closeRequesterFilterPanel();
    });

    this.shadowRootRef.getElementById('closeRequesterFilterBtn2')?.addEventListener('click', () => {
      this.closeRequesterFilterPanel();
    });

    this.shadowRootRef
      .getElementById('requesterSearchInput')
      ?.addEventListener('input', (event) => {
        const input = event.target as HTMLInputElement;
        this.requestUserFilterQuery = input.value.trim().toLowerCase();
        this.renderRequesterFilterList();
      });

    this.shadowRootRef
      .getElementById('requestsUserFilterList')
      ?.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        const option = target?.closest('[data-user-option]') as HTMLButtonElement | null;
        if (!option) return;
        const nextFilter = option.dataset.userOption;
        if (!nextFilter) return;
        this.requestUserFilterDraft = this.toggleFilterSelection(
          this.requestUserFilterDraft,
          nextFilter
        );
        this.renderRequesterFilterList();
        this.updateFilterPanelMeta();
      });

    this.shadowRootRef
      .getElementById('requestsUserFilterBackdrop')
      ?.addEventListener('click', () => {
        this.closeRequesterFilterPanel();
      });

    this.shadowRootRef.getElementById('filterUserSectionToggle')?.addEventListener('click', () => {
      this.filterUserSectionOpen = !this.filterUserSectionOpen;
      this.renderRequesterFilterList();
    });

    this.shadowRootRef
      .getElementById('filterStatusSectionToggle')
      ?.addEventListener('click', () => {
        this.filterStatusSectionOpen = !this.filterStatusSectionOpen;
        this.renderStatusFilterList();
      });

    this.shadowRootRef
      .getElementById('requestsStatusFilterList')
      ?.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        const option = target?.closest('[data-status-option]') as HTMLButtonElement | null;
        if (!option) return;
        const nextFilter = option.dataset.statusOption;
        if (!nextFilter) return;
        this.requestStatusFilterDraft = this.toggleFilterSelection(
          this.requestStatusFilterDraft,
          nextFilter
        );
        this.renderStatusFilterList();
        this.updateFilterPanelMeta();
      });

    this.shadowRootRef.getElementById('clearRequesterFilterBtn')?.addEventListener('click', () => {
      this.requestUserFilterDraft = ['all'];
      this.requestStatusFilterDraft = ['all'];
      this.requestUserFilterQuery = '';
      const searchInput = this.shadowRootRef.getElementById(
        'requesterSearchInput'
      ) as HTMLInputElement | null;
      if (searchInput) searchInput.value = '';
      this.renderRequesterFilterList();
      this.renderStatusFilterList();
      this.updateFilterPanelMeta();
    });

    this.shadowRootRef.getElementById('applyRequesterFilterBtn')?.addEventListener('click', () => {
      this.requestUserFilters = this.requestUserFilterDraft.slice();
      this.requestStatusFilters = this.requestStatusFilterDraft.slice();
      if (this.requestStatusFilters.length === 1) {
        this.requestStatusFilter = this.requestStatusFilters[0] as
          'all' | 'updated' | WidgetRequestItem['status'];
      } else {
        this.requestStatusFilter = 'all';
      }
      this.requestPage = 1;
      this.ensureVisibleSelection();
      this.renderRequesterChips();
      this.renderStatusChips();
      this.renderRequestList();
      this.closeRequesterFilterPanel();
    });

    this.shadowRootRef.getElementById('requestsPager')?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const action = target?.getAttribute('data-page-action');
      if (!action) return;
      const totalPages = this.getRequestTotalPages();
      if (action === 'prev' && this.requestPage > 1) {
        this.requestPage -= 1;
      }
      if (action === 'next' && this.requestPage < totalPages) {
        this.requestPage += 1;
      }
      this.renderRequestList();
    });

    this.shadowRootRef.getElementById('requestDetail')?.addEventListener('click', async (event) => {
      const target = event.target as HTMLElement | null;
      if (!target || !this.selectedRequest || !this.api) return;
      if (target.id === 'detailBackBtn') {
        this.setDashboardTab('table');
        return;
      }
      if (target.id === 'detailExportImageBtn') {
        void this.downloadSelectedRequestImage();
        return;
      }
      if (target.id === 'detailExportPdfBtn') {
        this.exportSelectedRequestPdf();
        return;
      }
      if (target.id === 'detailExportExcelBtn') {
        this.exportSelectedRequestExcel();
        return;
      }
      if (target.id === 'deleteBtn') {
        await this.deleteRequestById(this.selectedRequest.id);
        return;
      }
      if (target.id === 'retentionRequestToggle') {
        this.shadowRootRef.getElementById('retentionRequestForm')?.classList.remove('hidden');
        return;
      }
      if (target.id === 'retentionCancelBtn') {
        this.shadowRootRef.getElementById('retentionRequestForm')?.classList.add('hidden');
        return;
      }
      if (target.id === 'retentionSubmitBtn') {
        await this.submitRetentionExtension();
        return;
      }
      const adminStatus = this.shadowRootRef.getElementById('adminStatus');
      if (!this.adminActionToken) {
        try {
          const verify = await this.api.adminVerify();
          this.adminActionToken = verify.token;
        } catch {
          if (adminStatus) adminStatus.textContent = 'Please login as admin first.';
          return;
        }
      }

      try {
        const actionResult = await this.runAdminAction(target.id);
        this.applyLocalRequestMutation(actionResult.actionType, actionResult.comment);
        this.renderRequestList();
        this.renderSelectedRequestDetail();
        this.publishSyncMessage({
          type: 'request-updated',
          requestId: this.selectedRequest.id,
          ts: Date.now(),
        });
        if (adminStatus) adminStatus.textContent = 'Action successful.';
        this.showToast('Request action completed.', 'success');
        void this.refreshRequestsInBackground();
      } catch (error) {
        if (adminStatus) adminStatus.textContent = `Action failed: ${(error as Error).message}`;
        this.showToast(`Action failed: ${(error as Error).message}`, 'error');
      }
    });

    this.bindSnippetEvents();
    this.bindAnnotationEvents();
    this.setAdminSessionUI(this.adminSessionActive);
  }

  private async ensureVerifiedAdminSession(): Promise<void> {
    if (!this.api) {
      throw new Error('API client unavailable');
    }
    if (!this.adminEmail.trim() || !this.adminPassword.trim()) {
      throw new Error('Email and password are required');
    }

    await this.api.adminLogin({ email: this.adminEmail, password: this.adminPassword });
    await this.api.getAdminSession();
    const verify = await this.api.adminVerify();
    this.adminActionToken = verify.token;
    this.adminSessionActive = true;
  }

  private async runAdminAction(
    actionId: string,
    retriedAfterVerify = false
  ): Promise<{ actionType: 'approve' | 'reject' | 'comment'; comment?: string }> {
    if (!this.api || !this.selectedRequest || !this.adminActionToken) {
      throw new Error('Missing admin context for action');
    }

    const requestId = this.selectedRequest.id;
    const comment =
      (this.shadowRootRef.getElementById('adminCommentInput') as HTMLInputElement | null)?.value ||
      '';

    try {
      if (actionId === 'approveBtn') {
        await this.api.approveRequest(requestId, this.adminActionToken);
        return { actionType: 'approve' };
      }
      if (actionId === 'rejectBtn') {
        if (!comment.trim()) throw new Error('Reject reason is required');
        await this.api.rejectRequest(requestId, this.adminActionToken, comment);
        return { actionType: 'reject', comment };
      }
      if (actionId === 'commentBtn') {
        if (!comment.trim()) throw new Error('Comment is required');
        await this.api.commentRequest(requestId, this.adminActionToken, comment);
        return { actionType: 'comment', comment };
      }
      throw new Error('Unknown action');
    } catch (error) {
      const message = (error as Error).message.toLowerCase();
      const shouldRefreshVerification =
        message.includes('verification required') ||
        message.includes('invalid admin action token') ||
        message.includes('x-admin-action-token is required');
      if (!retriedAfterVerify && shouldRefreshVerification) {
        try {
          const verify = await this.api.adminVerify();
          this.adminActionToken = verify.token;
          return this.runAdminAction(actionId, true);
        } catch {
          throw new Error('Admin session expired. Please login again.');
        }
      }
      throw error;
    }
  }

  private bindSnippetEvents(): void {
    const sr = this.shadowRootRef;
    const snipCanvas = sr.getElementById('snipCanvas') as HTMLCanvasElement | null;
    const snipRect = sr.getElementById('snipRect') as HTMLDivElement | null;
    const snipConfirm = sr.getElementById('snipConfirm') as HTMLButtonElement | null;
    if (!snipCanvas || !snipRect || !snipConfirm) return;

    sr.getElementById('snipCancel')?.addEventListener('click', () => {
      this.closeSnippetSelector();
    });

    snipConfirm.addEventListener('click', async () => {
      if (!this.snipSelection) return;
      const cropped = this.cropCanvas(snipCanvas, this.snipSelection);
      const status = this.shadowRootRef.getElementById('captureState');
      this.closeSnippetSelector();
      if (status) {
        status.textContent =
          'Snippet selected. Highlighter mode enabled. Click Done when finished.';
      }
      this.annotTool = 'highlight';
      this.openAnnotationEditor(cropped);
    });

    const getPos = (e: MouseEvent): { x: number; y: number } => {
      const rect = snipCanvas.getBoundingClientRect();
      const scaleX = snipCanvas.width / rect.width;
      const scaleY = snipCanvas.height / rect.height;
      const x = Math.max(0, Math.min(snipCanvas.width, (e.clientX - rect.left) * scaleX));
      const y = Math.max(0, Math.min(snipCanvas.height, (e.clientY - rect.top) * scaleY));
      return { x, y };
    };

    const updateSelection = (x2: number, y2: number): void => {
      const x = Math.min(this.snipStartX, x2);
      const y = Math.min(this.snipStartY, y2);
      const width = Math.abs(x2 - this.snipStartX);
      const height = Math.abs(y2 - this.snipStartY);
      this.snipSelection = { x, y, width, height };

      const canvasRect = snipCanvas.getBoundingClientRect();
      const pxX = canvasRect.width / snipCanvas.width;
      const pxY = canvasRect.height / snipCanvas.height;
      // The canvas is centred inside its (padded) wrapper, so the selection
      // rectangle must be offset by the canvas position within the wrapper —
      // otherwise the box is drawn shifted away from the cursor.
      snipRect.style.left = `${snipCanvas.offsetLeft + x * pxX}px`;
      snipRect.style.top = `${snipCanvas.offsetTop + y * pxY}px`;
      snipRect.style.width = `${width * pxX}px`;
      snipRect.style.height = `${height * pxY}px`;
      snipRect.classList.remove('hidden');
      snipConfirm.disabled = width < 5 || height < 5;
    };

    snipCanvas.addEventListener('mousedown', (e) => {
      const p = getPos(e);
      this.snipDrawing = true;
      this.snipStartX = p.x;
      this.snipStartY = p.y;
      this.snipSelection = null;
      snipRect.classList.add('hidden');
      snipConfirm.disabled = true;
    });

    snipCanvas.addEventListener('mousemove', (e) => {
      if (!this.snipDrawing) return;
      const p = getPos(e);
      updateSelection(p.x, p.y);
    });

    const stopDrawing = () => {
      this.snipDrawing = false;
    };
    snipCanvas.addEventListener('mouseup', stopDrawing);
    snipCanvas.addEventListener('mouseleave', stopDrawing);
  }

  private bindAnnotationEvents(): void {
    const sr = this.shadowRootRef;

    // Distinct cursor per tool so the pointer changes when the tool changes.
    const svgCursor = (svg: string, hotX: number, hotY: number) =>
      `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${hotX} ${hotY}, crosshair`;
    const toolCursors: Record<AnnotTool, string> = {
      pen: svgCursor(
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M3 21l3.5-.8L20 6.7a2 2 0 0 0 0-2.8l-1-1a2 2 0 0 0-2.8 0L2.8 16.5 2 20z" fill="#1f5eff" stroke="#fff" stroke-width="1"/></svg>`,
        2,
        22
      ),
      highlight: svgCursor(
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M4 20h5l10.5-10.6a2 2 0 0 0 0-2.8l-2.1-2.1a2 2 0 0 0-2.8 0L4 15.2z" fill="#f4b400" stroke="#7a5b00" stroke-width="1"/><rect x="3" y="21" width="8" height="2" fill="#f4b400"/></svg>`,
        3,
        21
      ),
      eraser: svgCursor(
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="4" y="9" width="13" height="8" rx="1.5" transform="rotate(-30 10 13)" fill="#fff" stroke="#334" stroke-width="1.4"/></svg>`,
        4,
        12
      ),
    };
    // Tool buttons
    const setTool = (tool: AnnotTool) => {
      this.annotTool = tool;
      sr.getElementById('annotPen')?.classList.toggle('active', tool === 'pen');
      sr.getElementById('annotHighlight')?.classList.toggle('active', tool === 'highlight');
      sr.getElementById('annotEraser')?.classList.toggle('active', tool === 'eraser');
      const drawCanvas = sr.getElementById('annotDrawCanvas') as HTMLCanvasElement | null;
      if (drawCanvas) drawCanvas.style.cursor = toolCursors[tool];
    };
    sr.getElementById('annotPen')?.addEventListener('click', () => setTool('pen'));
    sr.getElementById('annotHighlight')?.addEventListener('click', () => setTool('highlight'));
    sr.getElementById('annotEraser')?.addEventListener('click', () => setTool('eraser'));
    // Apply the initial tool cursor (defaults to pen).
    setTool(this.annotTool);

    // Color & size
    sr.getElementById('annotColor')?.addEventListener('input', (e) => {
      this.annotColor = (e.target as HTMLInputElement).value;
    });
    sr.getElementById('annotSize')?.addEventListener('input', (e) => {
      this.annotSize = Number((e.target as HTMLInputElement).value);
    });

    // Undo
    sr.getElementById('annotUndo')?.addEventListener('click', () => this.annotUndo());

    // Keyboard undo
    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        const overlay = sr.getElementById('annotOverlay');
        if (overlay && !overlay.classList.contains('hidden')) this.annotUndo();
      }
    });

    // Done / Retake
    sr.getElementById('annotDone')?.addEventListener('click', () => this.annotConfirm());
    sr.getElementById('annotRetake')?.addEventListener('click', () => this.annotClose());

    // Drawing on the draw canvas
    const drawCanvas = sr.getElementById('annotDrawCanvas') as HTMLCanvasElement | null;
    if (!drawCanvas) return;

    const getPos = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
      const r = drawCanvas.getBoundingClientRect();
      const scaleX = drawCanvas.width / r.width;
      const scaleY = drawCanvas.height / r.height;
      return { x: (e.clientX - r.left) * scaleX, y: (e.clientY - r.top) * scaleY };
    };

    const startDraw = (x: number, y: number) => {
      const ctx = drawCanvas.getContext('2d');
      if (!ctx) return;
      // Save state for undo before each stroke
      this.annotUndoStack.push(ctx.getImageData(0, 0, drawCanvas.width, drawCanvas.height));
      if (this.annotUndoStack.length > 30) this.annotUndoStack.shift();
      this.annotDrawing = true;
      this.annotLastX = x;
      this.annotLastY = y;
    };

    const draw = (x: number, y: number) => {
      if (!this.annotDrawing) return;
      const ctx = drawCanvas.getContext('2d');
      if (!ctx) return;

      ctx.save();
      if (this.annotTool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
        ctx.lineWidth = this.annotSize * 3;
      } else if (this.annotTool === 'highlight') {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = this.hexToRgba(this.annotColor, 0.35);
        ctx.lineWidth = this.annotSize * 5;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = this.annotColor;
        ctx.lineWidth = this.annotSize;
      }
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(this.annotLastX, this.annotLastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();

      this.annotLastX = x;
      this.annotLastY = y;
    };

    const stopDraw = () => {
      this.annotDrawing = false;
    };

    // Mouse
    drawCanvas.addEventListener('mousedown', (e) => {
      const p = getPos(e);
      startDraw(p.x, p.y);
    });
    drawCanvas.addEventListener('mousemove', (e) => {
      const p = getPos(e);
      draw(p.x, p.y);
    });
    drawCanvas.addEventListener('mouseup', stopDraw);
    drawCanvas.addEventListener('mouseleave', stopDraw);

    // Touch
    drawCanvas.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        const p = getPos(e.touches[0]);
        startDraw(p.x, p.y);
      },
      { passive: false }
    );
    drawCanvas.addEventListener(
      'touchmove',
      (e) => {
        e.preventDefault();
        const p = getPos(e.touches[0]);
        draw(p.x, p.y);
      },
      { passive: false }
    );
    drawCanvas.addEventListener('touchend', stopDraw);
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  private annotUndo(): void {
    const drawCanvas = this.shadowRootRef.getElementById(
      'annotDrawCanvas'
    ) as HTMLCanvasElement | null;
    if (!drawCanvas) return;
    const ctx = drawCanvas.getContext('2d');
    if (!ctx) return;
    if (this.annotUndoStack.length > 0) {
      const prev = this.annotUndoStack.pop()!;
      ctx.putImageData(prev, 0, 0);
    } else {
      ctx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
    }
  }

  private annotClose(): void {
    const overlay = this.shadowRootRef.getElementById('annotOverlay');
    overlay?.classList.add('hidden');
    this.annotUndoStack = [];
  }

  private annotConfirm(): void {
    const bgCanvas = this.shadowRootRef.getElementById('annotBgCanvas') as HTMLCanvasElement | null;
    const drawCanvas = this.shadowRootRef.getElementById(
      'annotDrawCanvas'
    ) as HTMLCanvasElement | null;
    const status = this.shadowRootRef.getElementById('captureState');
    if (!bgCanvas || !drawCanvas) return;

    // Merge draw layer onto bg canvas
    const ctx = bgCanvas.getContext('2d');
    ctx?.drawImage(drawCanvas, 0, 0);

    bgCanvas.toBlob((blob) => {
      this.screenshotBlob = blob ?? undefined;
      if (status)
        status.textContent = this.screenshotBlob
          ? 'Screenshot attached ✓'
          : 'Could not save screenshot';
    }, 'image/png');

    this.annotClose();
  }

  private openAnnotationEditor(composite: HTMLCanvasElement): void {
    const overlay = this.shadowRootRef.getElementById('annotOverlay');
    const bgCanvas = this.shadowRootRef.getElementById('annotBgCanvas') as HTMLCanvasElement | null;
    const drawCanvas = this.shadowRootRef.getElementById(
      'annotDrawCanvas'
    ) as HTMLCanvasElement | null;
    if (!overlay || !bgCanvas || !drawCanvas) return;

    // Size canvases to match the composite
    bgCanvas.width = composite.width;
    bgCanvas.height = composite.height;
    drawCanvas.width = composite.width;
    drawCanvas.height = composite.height;

    // Copy composite into bg canvas
    const ctx = bgCanvas.getContext('2d');
    ctx?.drawImage(composite, 0, 0);

    // Clear draw canvas
    drawCanvas.getContext('2d')?.clearRect(0, 0, drawCanvas.width, drawCanvas.height);

    // Reset undo stack and apply the currently selected default tool
    this.annotUndoStack = [];
    this.shadowRootRef
      .getElementById('annotPen')
      ?.classList.toggle('active', this.annotTool === 'pen');
    this.shadowRootRef
      .getElementById('annotHighlight')
      ?.classList.toggle('active', this.annotTool === 'highlight');
    this.shadowRootRef
      .getElementById('annotEraser')
      ?.classList.toggle('active', this.annotTool === 'eraser');
    drawCanvas.style.cursor = this.annotTool === 'eraser' ? 'cell' : 'crosshair';

    overlay.classList.remove('hidden');
  }

  private openSnippetSelector(sourceCanvas: HTMLCanvasElement): void {
    const overlay = this.shadowRootRef.getElementById('snipOverlay');
    const snipCanvas = this.shadowRootRef.getElementById('snipCanvas') as HTMLCanvasElement | null;
    const snipRect = this.shadowRootRef.getElementById('snipRect');
    const snipConfirm = this.shadowRootRef.getElementById(
      'snipConfirm'
    ) as HTMLButtonElement | null;
    if (!overlay || !snipCanvas || !snipConfirm) return;

    snipCanvas.width = sourceCanvas.width;
    snipCanvas.height = sourceCanvas.height;
    const ctx = snipCanvas.getContext('2d');
    ctx?.drawImage(sourceCanvas, 0, 0);

    this.snipSelection = null;
    snipConfirm.disabled = true;
    snipRect?.classList.add('hidden');
    overlay.classList.remove('hidden');
  }

  private closeSnippetSelector(): void {
    const overlay = this.shadowRootRef.getElementById('snipOverlay');
    overlay?.classList.add('hidden');
    this.snipDrawing = false;
    this.snipSelection = null;
  }

  private cropCanvas(
    source: HTMLCanvasElement,
    selection: { x: number; y: number; width: number; height: number }
  ): HTMLCanvasElement {
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.floor(selection.width));
    out.height = Math.max(1, Math.floor(selection.height));
    const ctx = out.getContext('2d');
    ctx?.drawImage(
      source,
      Math.floor(selection.x),
      Math.floor(selection.y),
      Math.floor(selection.width),
      Math.floor(selection.height),
      0,
      0,
      out.width,
      out.height
    );
    return out;
  }

  private syncOpenState(): void {
    const panel = this.shadowRootRef.getElementById('panel');
    if (!panel) return;
    panel.classList.toggle('open', this.isOpen);
  }

  private applyResolvedTheme(theme: 'light' | 'dark'): void {
    const root = this.shadowRootRef.querySelector('.faas-widget');
    if (root) {
      root.classList.toggle('light', theme === 'light');
      root.classList.toggle('dark', theme === 'dark');
    }
  }

  private setTheme(theme: 'light' | 'dark', _source: 'user' | 'app' = 'user'): void {
    this.selectedTheme = theme;
    this.applyResolvedTheme(theme);
    this.updateThemeToggleState();
  }

  private detectAppTheme(): 'light' | 'dark' {
    const html = document.documentElement;
    const body = document.body;
    const elements = [html, body].filter((element): element is HTMLElement => Boolean(element));

    for (const element of elements) {
      const dataTheme = (element.getAttribute('data-theme') || '').toLowerCase();
      if (dataTheme === 'dark') return 'dark';
      if (dataTheme === 'light') return 'light';

      const dataMode = (element.getAttribute('data-mode') || '').toLowerCase();
      if (dataMode === 'dark') return 'dark';
      if (dataMode === 'light') return 'light';

      if (
        element.classList.contains('dark') ||
        element.classList.contains('theme-dark') ||
        element.classList.contains('dark-mode')
      ) {
        return 'dark';
      }
      if (
        element.classList.contains('light') ||
        element.classList.contains('theme-light') ||
        element.classList.contains('light-mode')
      ) {
        return 'light';
      }

      const colorScheme = (element.style.colorScheme || '').toLowerCase();
      if (colorScheme.includes('dark')) return 'dark';
      if (colorScheme.includes('light')) return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private setupAppThemeObserver(): void {
    if (this.appThemeObserver) return;
    const syncTheme = () => {
      if (!this.followAppTheme) return;
      const appTheme = this.detectAppTheme();
      if (appTheme !== this.selectedTheme) {
        this.setTheme(appTheme, 'app');
      }
    };

    this.appThemeObserver = new MutationObserver(() => {
      syncTheme();
    });

    const observerOptions: MutationObserverInit = {
      attributes: true,
      attributeFilter: ['class', 'data-theme', 'data-mode', 'style'],
    };

    this.appThemeObserver.observe(document.documentElement, observerOptions);
    if (document.body) {
      this.appThemeObserver.observe(document.body, observerOptions);
    }

    syncTheme();
  }

  private teardownAppThemeObserver(): void {
    if (!this.appThemeObserver) return;
    this.appThemeObserver.disconnect();
    this.appThemeObserver = undefined;
  }

  private updateThemeToggleState(): void {
    const lightBtn = this.shadowRootRef.getElementById('themeLightBtn') as HTMLButtonElement | null;
    const darkBtn = this.shadowRootRef.getElementById('themeDarkBtn') as HTMLButtonElement | null;
    if (!lightBtn || !darkBtn) return;
    const isLight = this.selectedTheme === 'light';
    const isDark = this.selectedTheme === 'dark';
    lightBtn.classList.toggle('active', isLight);
    darkBtn.classList.toggle('active', isDark);
    lightBtn.setAttribute('aria-pressed', String(isLight));
    darkBtn.setAttribute('aria-pressed', String(isDark));
  }

  private switchTab(tab: 'capture' | 'requests'): void {
    if (tab === 'requests' && !this.canViewRequestsTab) {
      this.shadowRootRef.getElementById('captureView')?.classList.remove('hidden');
      this.shadowRootRef.getElementById('requestsView')?.classList.add('hidden');
      return;
    }
    const tabCapture = this.shadowRootRef.getElementById('tabCapture');
    const tabRequests = this.shadowRootRef.getElementById('tabRequests');
    this.shadowRootRef.getElementById('captureView')?.classList.toggle('hidden', tab !== 'capture');
    this.shadowRootRef
      .getElementById('requestsView')
      ?.classList.toggle('hidden', tab !== 'requests');
    tabCapture?.classList.toggle('active-tab', tab === 'capture');
    tabRequests?.classList.toggle('active-tab', tab === 'requests');
    if (tab !== 'requests') {
      this.closeDashboardDialog();
    }
  }

  private setAdminSessionUI(isLoggedIn: boolean): void {
    this.adminSessionActive = isLoggedIn;
    this.shadowRootRef.getElementById('adminLoginSection')?.classList.toggle('hidden', isLoggedIn);
    this.shadowRootRef.getElementById('dashboardActions')?.classList.remove('hidden');
    this.shadowRootRef.getElementById('adminLogoutBtn')?.classList.toggle('hidden', !isLoggedIn);
    if (!isLoggedIn) {
      this.closeRequesterFilterPanel();
    }
    this.renderRequesterChips();
    if (!isLoggedIn) {
      this.stopRequestsPolling();
      return;
    }
    if (this.isDashboardOpen()) {
      this.startRequestsPolling();
    }
  }

  private normalizeUiErrorMessage(error: unknown): string {
    let message = (error as Error)?.message || String(error || 'Unknown error');
    try {
      const parsed = JSON.parse(message) as { detail?: unknown; message?: unknown };
      if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
        message = parsed.detail;
      } else if (typeof parsed.message === 'string' && parsed.message.trim()) {
        message = parsed.message;
      }
    } catch {
      // Keep original message when response is not JSON.
    }

    const lowered = message.toLowerCase();
    if (lowered.includes('generated integration admin credentials')) {
      return 'Use integration admin email/password from Admin Integration list (not admin@example.com).';
    }
    if (lowered.includes('invalid email or password')) {
      return 'Invalid admin email or password. Use integration admin credentials from Admin Integration list.';
    }
    return message;
  }

  private isDashboardOpen(): boolean {
    const dialog = this.shadowRootRef.getElementById('dashboardDialog');
    if (!dialog) return false;
    if (dialog.classList.contains('hidden')) return false;
    if (dialog.getAttribute('aria-hidden') === 'true') return false;
    return true;
  }

  private showToast(message: string, tone: 'success' | 'error' | 'info' = 'info'): void {
    const toast = this.shadowRootRef.getElementById('widgetToast');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.remove('success', 'error', 'info', 'visible');
    toast.classList.add(tone, 'visible');

    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }
    this.toastTimer = window.setTimeout(() => {
      toast.classList.remove('visible', 'success', 'error', 'info');
      this.toastTimer = undefined;
    }, 2600);
  }

  private logoutAdmin(): void {
    this.adminSessionActive = false;
    this.adminActionToken = undefined;
    this.adminPassword = '';
    this.selectedRequest = undefined;
    this.selectedRequestId = undefined;
    this.requests = [];
    this.api?.clearAdminSession();
    this.closeDashboardDialog();
    this.setAdminSessionUI(false);
    const adminStatus = this.shadowRootRef.getElementById('adminStatus');
    if (adminStatus) adminStatus.textContent = 'Logged out.';
    this.showToast('Logged out.', 'info');
    const detail = this.shadowRootRef.getElementById('requestDetail');
    if (detail) detail.innerHTML = '';
    const list = this.shadowRootRef.getElementById('requestsList');
    if (list) list.innerHTML = '';
  }

  private openDashboardDialog(): void {
    const dialog = this.shadowRootRef.getElementById('dashboardDialog');
    dialog?.classList.remove('hidden');
    dialog?.setAttribute('aria-hidden', 'false');
    // Neutralize any ancestor transform (center-position panels / half-hidden
    // root) so the fixed dialog stays centered on the viewport, not the panel.
    this.shadowRootRef.querySelector('.faas-widget')?.classList.add('dashboard-open');
    this.setDashboardTab('table');
    this.startRequestsPolling();
    if (this.requests.length === 0) {
      void this.loadRequestsView();
    }
  }

  private setDashboardTab(tab: DashboardTab): void {
    this.activeDashboardTab = tab;
    const dashboardBody = this.shadowRootRef.getElementById('dashboardBody');
    const tablePane = this.shadowRootRef.getElementById('dashboardTablePane');
    const detailPane = this.shadowRootRef.getElementById('requestDetail');
    const tableTab = this.shadowRootRef.getElementById('dashboardTabTable');
    const detailTab = this.shadowRootRef.getElementById('dashboardTabDetail');

    dashboardBody?.classList.toggle('mode-table', tab === 'table');
    dashboardBody?.classList.toggle('mode-detail', tab === 'detail');
    tablePane?.classList.toggle('hidden', tab !== 'table');
    detailPane?.classList.toggle('hidden', tab !== 'detail');
    tableTab?.classList.toggle('active', tab === 'table');
    detailTab?.classList.toggle('active', tab === 'detail');
    tableTab?.setAttribute('aria-selected', tab === 'table' ? 'true' : 'false');
    detailTab?.setAttribute('aria-selected', tab === 'detail' ? 'true' : 'false');

    if (tab === 'detail' && !this.selectedRequest) {
      const detail = this.shadowRootRef.getElementById('requestDetail');
      if (detail)
        detail.innerHTML =
          '<p class="hint">Select a request from the table to view and manage details.</p>';
    }
  }

  private closeDashboardDialog(): void {
    const dialog = this.shadowRootRef.getElementById('dashboardDialog');
    dialog?.classList.add('hidden');
    dialog?.setAttribute('aria-hidden', 'true');
    this.shadowRootRef.querySelector('.faas-widget')?.classList.remove('dashboard-open');
    this.closeRequesterFilterPanel();
    this.stopRequestsPolling();
  }

  private startRequestsPolling(): void {
    this.stopRequestsPolling();
    if (!this.adminSessionActive) return;
    this.requestsPollTimer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (!this.isDashboardOpen()) return;
      void this.refreshRequestsInBackground();
    }, this.requestPollIntervalMs);
  }

  private stopRequestsPolling(): void {
    if (this.requestsPollTimer) {
      window.clearInterval(this.requestsPollTimer);
      this.requestsPollTimer = undefined;
    }
  }

  private async refreshRequestsInBackground(): Promise<void> {
    if (this.isRefreshingRequests) return;
    const now = Date.now();
    if (now - this.lastBackgroundRefreshAt < this.minBackgroundRefreshMs) return;
    this.lastBackgroundRefreshAt = now;
    this.isRefreshingRequests = true;
    try {
      await this.loadRequestsView(true);
      if (this.activeDashboardTab === 'detail' && this.selectedRequestId) {
        await this.loadRequestDetail(this.selectedRequestId);
      }
    } finally {
      this.isRefreshingRequests = false;
    }
  }

  private async loadRequestsView(silent = false): Promise<void> {
    const requestsList = this.shadowRootRef.getElementById('requestsList');
    if (!requestsList || !this.api) return;
    this.applyOptionalUserEmailContext();
    if (!silent) {
      requestsList.textContent = 'Loading...';
    }
    try {
      this.requests = await this.api.listWidgetRequests();
      this.initializeSeenRequestUpdatesBaseline();
      this.ensureVisibleSelection();
      this.renderStatusChips();
      this.renderRequesterChips();
      this.renderRequestList();
      this.updateRequestsTabBadge();
      if (
        this.selectedRequestId &&
        (!this.selectedRequest || this.selectedRequest.id !== this.selectedRequestId)
      ) {
        await this.loadRequestDetail(this.selectedRequestId);
      } else if (!this.selectedRequestId) {
        const detail = this.shadowRootRef.getElementById('requestDetail');
        if (detail)
          detail.innerHTML = '<p class="hint">No matching requests for the current filters.</p>';
      }
    } catch (error) {
      requestsList.innerHTML = `<p class="status error">Failed to load requests: ${(error as Error).message}</p>`;
    }
  }

  private renderRequestList(): void {
    const requestsList = this.shadowRootRef.getElementById('requestsList');
    const requestsPager = this.shadowRootRef.getElementById('requestsPager');
    if (!requestsList) return;
    const filteredRequests = this.getFilteredRequests();
    if (filteredRequests.length === 0) {
      requestsList.innerHTML = '<p class="hint">No requests yet.</p>';
      if (requestsPager) requestsPager.innerHTML = '';
      return;
    }

    const totalPages = this.getRequestTotalPages();
    if (this.requestPage > totalPages) {
      this.requestPage = totalPages;
    }
    const start = (this.requestPage - 1) * this.requestPageSize;
    const pageItems = filteredRequests.slice(start, start + this.requestPageSize);

    requestsList.innerHTML = `
			<table class="requests-table" aria-label="Request status table">
				<thead>
					<tr>
						<th>Request</th>
						<th>Submitted by</th>
						<th>Status</th>
						<th>Verification</th>
						<th>Latest comment</th>
						<th>Updated</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					${pageItems
            .map((item) => {
              const isSelected = this.selectedRequestId === item.id ? 'selected' : '';
              const title = this.escapeHtml(item.title || 'Untitled request');
              const requestId = this.escapeHtml(item.id);
              const submitter = this.escapeHtml(this.getRequesterLabel(item));
              const latestComment = this.escapeHtml(
                (item.latest_comment || '').trim() || 'No comment yet'
              );
              const statusLabel = this.escapeHtml(this.statusDisplayLabel(item.status));
              const hasNewUpdate = this.hasRequestNewUpdate(item);
              return `
								<tr data-request-id="${requestId}" class="request-table-row ${isSelected}">
									<td>
										<div class="request-title" title="${title}">${title}</div>
										<div class="request-sub" title="${requestId}">
											<span>${requestId}</span>
											${hasNewUpdate ? '<span class="request-update-badge">New update</span>' : ''}
										</div>
									</td>
									<td><span class="request-submitter" title="${submitter}">${submitter}</span></td>
									<td>
										${
                      this.adminSessionActive
                        ? `<select class="status-inline-select" data-status-select-request-id="${requestId}">
													<option value="submitted" ${item.status === 'submitted' ? 'selected' : ''}>submitted</option>
												<option value="in_review" ${item.status === 'in_review' ? 'selected' : ''}>in_review</option>
													<option value="needs_info" ${item.status === 'needs_info' ? 'selected' : ''}>needs_info</option>
												<option value="approved" ${item.status === 'approved' ? 'selected' : ''}>approved</option>
												<option value="rejected" ${item.status === 'rejected' ? 'selected' : ''}>rejected</option>
											</select>`
                        : `<span class="status-chip ${this.statusClass(item.status)}">${statusLabel}</span>`
                    }
									</td>
									<td>${this.escapeHtml(this.verificationLabel(item.status))}</td>
									<td>
										<div class="request-comment-preview" title="${latestComment}">${latestComment}</div>
									</td>
									<td>${this.formatTimestampCompact(item.updated_at || item.created_at)}</td>
									<td>
										${
                      this.adminSessionActive
                        ? `<button type="button" class="ghost table-delete-btn" data-delete-request-id="${requestId}" title="Delete request" aria-label="Delete request ${requestId}">
												<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
													<path d="M9 3H15L16 5H21V7H3V5H8L9 3Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
													<path d="M6 7H18L17 19C16.92 20.1 16.01 21 14.9 21H9.1C7.99 21 7.08 20.1 7 19L6 7Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
													<path d="M10 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
													<path d="M14 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
												</svg>
											</button>`
                        : '<span class="hint">Read only</span>'
                    }
									</td>
								</tr>
							`;
            })
            .join('')}
				</tbody>
			</table>
		`;

    if (requestsPager) {
      requestsPager.innerHTML = `
				<span class="pager-total">${filteredRequests.length} item(s)</span>
				<div class="pager-actions">
					<button type="button" class="ghost" data-page-action="prev" ${this.requestPage <= 1 ? 'disabled' : ''}>Prev</button>
					<span class="pager-page">${this.requestPage} / ${totalPages}</span>
					<button type="button" class="ghost" data-page-action="next" ${this.requestPage >= totalPages ? 'disabled' : ''}>Next</button>
				</div>
			`;
    }
  }

  private getFilteredRequests(): WidgetRequestItem[] {
    return this.requests.filter((item) => {
      const requesterKey = this.getRequesterKey(item);
      const requesterFilterMatch =
        this.requestUserFilters.includes('all') || this.requestUserFilters.includes(requesterKey);
      if (!requesterFilterMatch) return false;
      const statusMatch =
        this.requestStatusFilters.includes('all') ||
        this.requestStatusFilters.some((status) =>
          status === 'updated' ? this.hasRequestNewUpdate(item) : item.status === status
        );
      if (!statusMatch) return false;
      if (!this.requestSearchQuery) return true;
      const title = (item.title || '').toLowerCase();
      const requestId = (item.id || '').toLowerCase();
      const requester = this.getRequesterLabel(item).toLowerCase();
      return (
        title.includes(this.requestSearchQuery) ||
        requestId.includes(this.requestSearchQuery) ||
        requester.includes(this.requestSearchQuery)
      );
    });
  }

  private getRequesterLabel(item: WidgetRequestItem): string {
    const email = (item.submitted_by_email || '').trim();
    return email || item.submitted_by || 'Unknown user';
  }

  private getRequesterKey(item: WidgetRequestItem): string {
    return this.getRequesterLabel(item).toLowerCase();
  }

  private getRequesterCounts(): Array<{ key: string; label: string; count: number }> {
    const counts = new Map<string, { key: string; label: string; count: number }>();
    for (const item of this.requests) {
      const label = this.getRequesterLabel(item);
      const key = label.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(key, { key, label, count: 1 });
      }
    }
    return Array.from(counts.values()).sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label)
    );
  }

  private renderRequesterChips(): void {
    const summary = this.shadowRootRef.getElementById('requestsUserFilterSummary');
    const openBtn = this.shadowRootRef.getElementById('openRequesterFilterBtn');
    const counts = this.getRequesterCounts();
    const validUserSelections = this.requestUserFilters.filter(
      (key) => key === 'all' || counts.some((entry) => entry.key === key)
    );
    if (validUserSelections.length === 0) {
      this.requestUserFilters = ['all'];
    } else {
      this.requestUserFilters = validUserSelections.includes('all') ? ['all'] : validUserSelections;
    }
    if (!this.requestStatusFilters.length) {
      this.requestStatusFilters = ['all'];
    }

    const userSelectedCount = this.requestUserFilters.includes('all')
      ? 0
      : this.requestUserFilters.length;
    const statusSelectedCount = this.requestStatusFilters.includes('all')
      ? 0
      : this.requestStatusFilters.length;
    if (summary) {
      summary.classList.toggle('hidden', !this.adminSessionActive);
      const chips: string[] = [];
      chips.push(
        `<span class="chip active">Users: ${userSelectedCount === 0 ? 'All' : userSelectedCount}</span>`
      );
      chips.push(
        `<span class="chip active">Status: ${statusSelectedCount === 0 ? 'All' : statusSelectedCount}</span>`
      );
      summary.innerHTML = chips.join('');
    }
    if (openBtn) {
      const hasActive = userSelectedCount > 0 || statusSelectedCount > 0;
      openBtn.classList.toggle('active', this.requestUserFilterPanelOpen || hasActive);
      openBtn.setAttribute('aria-expanded', this.requestUserFilterPanelOpen ? 'true' : 'false');
      openBtn.classList.toggle('hidden', !this.adminSessionActive);
    }
    if (!this.adminSessionActive && this.requestUserFilterPanelOpen) {
      this.closeRequesterFilterPanel();
      return;
    }
    if (!this.requestUserFilterPanelOpen) {
      this.requestUserFilterDraft = this.requestUserFilters.slice();
      this.requestStatusFilterDraft = this.requestStatusFilters.slice();
      this.requestUserFilterQuery = '';
      const searchInput = this.shadowRootRef.getElementById(
        'requesterSearchInput'
      ) as HTMLInputElement | null;
      if (searchInput) searchInput.value = '';
    }
    this.renderRequesterFilterList();
    this.renderStatusFilterList();
  }

  private renderRequesterFilterList(): void {
    const list = this.shadowRootRef.getElementById('requestsUserFilterList');
    const toggle = this.shadowRootRef.getElementById('filterUserSectionToggle');
    if (!list) return;

    if (!this.filterUserSectionOpen) {
      list.classList.add('hidden');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.querySelector('.filter-section-chevron')?.classList.add('collapsed');
      return;
    }
    list.classList.remove('hidden');
    toggle?.setAttribute('aria-expanded', 'true');
    toggle?.querySelector('.filter-section-chevron')?.classList.remove('collapsed');

    const counts = this.getRequesterCounts();
    const query = this.requestUserFilterQuery.trim().toLowerCase();
    const filtered = query
      ? counts.filter((entry) => entry.label.toLowerCase().includes(query))
      : counts;
    const visible = query ? filtered : filtered.slice(0, 50);

    const checkSvg = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6l3 3 5-5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const allUsersSelected = this.requestUserFilterDraft.includes('all');
    const allOptionActive = allUsersSelected ? 'selected' : '';
    list.innerHTML = `
			<button type="button" class="user-filter-option ${allOptionActive}" data-user-option="all" role="option" aria-selected="${allUsersSelected ? 'true' : 'false'}">
				<span class="user-filter-option-check">${allUsersSelected ? checkSvg : ''}</span>
				<span class="user-filter-option-name">All users</span>
				<span class="user-filter-option-count">${this.requests.length}</span>
			</button>
			${visible
        .map((entry) => {
          const selected = this.requestUserFilterDraft.includes(entry.key);
          return `
						<button type="button" class="user-filter-option ${selected ? 'selected' : ''}" data-user-option="${this.escapeHtml(entry.key)}" role="option" aria-selected="${selected ? 'true' : 'false'}">
							<span class="user-filter-option-check">${selected ? checkSvg : ''}</span>
							<span class="user-filter-option-name" title="${this.escapeHtml(entry.label)}">${this.escapeHtml(entry.label)}</span>
							<span class="user-filter-option-count">${entry.count}</span>
						</button>
					`;
        })
        .join('')}
		`;
    this.updateFilterPanelMeta();
  }

  private renderStatusFilterList(): void {
    const list = this.shadowRootRef.getElementById('requestsStatusFilterList');
    const toggle = this.shadowRootRef.getElementById('filterStatusSectionToggle');
    if (!list) return;

    if (!this.filterStatusSectionOpen) {
      list.classList.add('hidden');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.querySelector('.filter-section-chevron')?.classList.add('collapsed');
      return;
    }
    list.classList.remove('hidden');
    toggle?.setAttribute('aria-expanded', 'true');
    toggle?.querySelector('.filter-section-chevron')?.classList.remove('collapsed');

    const checkSvg = `<svg viewBox="0 0 12 12" width="10" height="10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6l3 3 5-5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    list.innerHTML = this.requestStatusOptions
      .map(({ key, label }) => {
        const selected = this.requestStatusFilterDraft.includes(key);
        return `
					<button type="button" class="user-filter-option ${selected ? 'selected' : ''}" data-status-option="${key}" role="option" aria-selected="${selected}">
						<span class="user-filter-option-check">${selected ? checkSvg : ''}</span>
						<span class="user-filter-option-name">${this.escapeHtml(label)}</span>
					</button>
				`;
      })
      .join('');
  }

  private updateFilterPanelMeta(): void {
    const meta = this.shadowRootRef.getElementById('requestsUserFilterMeta');
    if (!meta) return;
    const userCount = this.requestUserFilterDraft.includes('all')
      ? 0
      : this.requestUserFilterDraft.length;
    const statusCount = this.requestStatusFilterDraft.includes('all')
      ? 0
      : this.requestStatusFilterDraft.length;
    const total = userCount + statusCount;
    meta.textContent = `Selected ${total} item(s)`;
  }

  private toggleFilterSelection(current: string[], next: string): string[] {
    if (next === 'all') return ['all'];
    const base = current.includes('all') ? [] : current.slice();
    const exists = base.includes(next);
    const updated = exists ? base.filter((item) => item !== next) : [...base, next];
    return updated.length === 0 ? ['all'] : updated;
  }

  private openRequesterFilterPanel(): void {
    if (!this.adminSessionActive) return;
    this.requestUserFilterPanelOpen = true;
    this.requestUserFilterDraft = this.requestUserFilters.slice();
    this.requestStatusFilterDraft = this.requestStatusFilters.slice();
    const panel = this.shadowRootRef.getElementById(
      'requestsUserFilterPanel'
    ) as HTMLElement | null;
    const backdrop = this.shadowRootRef.getElementById('requestsUserFilterBackdrop');
    const btn = this.shadowRootRef.getElementById('openRequesterFilterBtn');
    if (panel && btn) {
      const dashboardCard = this.shadowRootRef.querySelector(
        '.dashboard-card'
      ) as HTMLElement | null;
      if (dashboardCard) {
        const cardRect = dashboardCard.getBoundingClientRect();
        const panelWidth = Math.min(640, cardRect.width - 24, window.innerWidth - 24);
        const safeWidth = Math.max(320, panelWidth);
        panel.style.width = `${safeWidth}px`;
        panel.style.maxHeight = `${Math.max(320, Math.min(window.innerHeight - 16, cardRect.height - 24))}px`;
        panel.style.left = `${cardRect.left + cardRect.width / 2}px`;
        panel.style.top = `${cardRect.top + cardRect.height / 2}px`;
        panel.style.transform = 'translate(-50%, -50%)';
      } else {
        const panelWidth = Math.min(640, window.innerWidth - 24);
        panel.style.width = `${Math.max(320, panelWidth)}px`;
        panel.style.maxHeight = `${window.innerHeight - 16}px`;
        panel.style.left = '50%';
        panel.style.top = '50%';
        panel.style.transform = 'translate(-50%, -50%)';
      }
    }
    panel?.classList.remove('hidden');
    backdrop?.classList.remove('hidden');
    this.renderRequesterChips();
    this.renderStatusFilterList();
    this.updateFilterPanelMeta();
    const searchInput = this.shadowRootRef.getElementById(
      'requesterSearchInput'
    ) as HTMLInputElement | null;
    searchInput?.focus();
  }

  private closeRequesterFilterPanel(): void {
    this.requestUserFilterPanelOpen = false;
    this.requestUserFilterDraft = this.requestUserFilters.slice();
    this.requestStatusFilterDraft = this.requestStatusFilters.slice();
    const panel = this.shadowRootRef.getElementById('requestsUserFilterPanel');
    const backdrop = this.shadowRootRef.getElementById('requestsUserFilterBackdrop');
    panel?.classList.add('hidden');
    backdrop?.classList.add('hidden');
    this.renderRequesterChips();
  }

  private getRequestTotalPages(): number {
    const total = this.getFilteredRequests().length;
    return Math.max(1, Math.ceil(total / this.requestPageSize));
  }

  private ensureVisibleSelection(): void {
    const filtered = this.getFilteredRequests();
    if (filtered.length === 0) {
      this.selectedRequestId = undefined;
      this.selectedRequest = undefined;
      return;
    }
    if (!this.selectedRequestId || !filtered.some((item) => item.id === this.selectedRequestId)) {
      this.selectedRequestId = filtered[0].id;
    }
  }

  private renderStatusChips(): void {
    const chipsWrap = this.shadowRootRef.getElementById('requestsStatusChips');
    if (!chipsWrap) return;
    const updatedCount = this.getNewUpdatesCount();
    const updatedChip = chipsWrap.querySelector<HTMLButtonElement>('[data-status="updated"]');
    if (updatedChip) {
      updatedChip.textContent = updatedCount > 0 ? `New updates (${updatedCount})` : 'New updates';
    }
    chipsWrap.querySelectorAll<HTMLButtonElement>('[data-status]').forEach((chip) => {
      chip.classList.toggle('active', chip.dataset.status === this.requestStatusFilter);
    });
  }

  private updateRequestsTabBadge(): void {
    const badge = this.shadowRootRef.getElementById('requestsCountBadge');
    if (!badge) return;
    const total = this.requests.length;
    if (total > 0) {
      badge.textContent = String(total);
      badge.style.display = 'inline-block';
    } else {
      badge.textContent = '';
      badge.style.display = 'none';
    }
  }

  private verificationLabel(status: WidgetRequestItem['status']): string {
    if (status === 'approved') return 'Verified';
    if (status === 'rejected') return 'Rejected';
    if (status === 'needs_info') return 'Needs info';
    if (status === 'submitted') return 'Submitted';
    return 'Pending verification';
  }

  private async loadRequestDetail(requestId: string): Promise<void> {
    const detail = this.shadowRootRef.getElementById('requestDetail');
    if (!detail || !this.api) return;
    detail.textContent = 'Loading request detail...';
    try {
      this.selectedRequest = await this.api.getWidgetRequest(requestId);
      this.selectedRequestId = requestId;
      this.retentionPolicy = undefined;
      if (this.adminSessionActive && this.selectedRequest.image_url) {
        try {
          this.retentionPolicy = await this.api.getRetentionPolicy();
        } catch {
          this.retentionPolicy = null;
        }
      }
      this.markRequestAsSeen(
        requestId,
        this.selectedRequest.updated_at || this.selectedRequest.created_at
      );
      const index = this.requests.findIndex((item) => item.id === this.selectedRequest?.id);
      if (index >= 0 && this.selectedRequest) {
        this.requests[index] = {
          ...this.requests[index],
          status: this.selectedRequest.status,
          title: this.selectedRequest.title,
          description: this.selectedRequest.description,
          updated_at: this.selectedRequest.updated_at,
        };
      }
      this.renderRequestList();
      this.renderStatusChips();
      this.renderRequesterChips();
      this.renderSelectedRequestDetail();
    } catch (error) {
      detail.innerHTML = `<p class="status error">Failed to load request detail: ${(error as Error).message}</p>`;
    }
  }

  /**
   * Compute how much of the image-retention window is left for the selected
   * request. Mirrors the admin portal: images are removed on day
   * (retention + 1) after creation; the banner only shows within the warning
   * threshold (or once expired).
   */
  private computeRetentionState(): {
    show: boolean;
    daysLeft: number;
    deletionDate: Date | null;
    effectiveDays: number;
  } {
    const req = this.selectedRequest;
    const threshold = this.retentionPolicy?.warning_threshold_days ?? 30;
    const effectiveDays =
      this.retentionPolicy?.effective_retention_days ??
      (typeof req?.image_retention_days === 'number' ? req.image_retention_days : 0);
    const createdAt = req?.created_at;
    if (!createdAt || !Number.isFinite(effectiveDays) || effectiveDays <= 0) {
      return { show: false, daysLeft: Number.POSITIVE_INFINITY, deletionDate: null, effectiveDays };
    }
    const created = new Date(createdAt);
    if (Number.isNaN(created.getTime())) {
      return { show: false, daysLeft: Number.POSITIVE_INFINITY, deletionDate: null, effectiveDays };
    }
    const deletionDate = new Date(created.getTime());
    deletionDate.setDate(deletionDate.getDate() + effectiveDays + 1);
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil((deletionDate.getTime() - Date.now()) / msPerDay);
    return { show: daysLeft <= threshold, daysLeft, deletionDate, effectiveDays };
  }

  private async submitRetentionExtension(): Promise<void> {
    if (!this.api || !this.selectedRequest) return;
    const form = this.shadowRootRef.getElementById('retentionRequestForm');
    const daysEl = this.shadowRootRef.getElementById(
      'retentionDaysSelect'
    ) as HTMLSelectElement | null;
    const reasonEl = this.shadowRootRef.getElementById(
      'retentionReasonInput'
    ) as HTMLTextAreaElement | null;
    const statusEl = this.shadowRootRef.getElementById('retentionRequestStatus');
    const requestedDays = Number(daysEl?.value || 30);
    const reason = (reasonEl?.value || '').trim();
    if (reason.length < 3) {
      if (statusEl) {
        statusEl.textContent = 'Please describe the reason for the extension.';
        statusEl.classList.add('error');
      }
      return;
    }
    const submitBtn = this.shadowRootRef.getElementById(
      'retentionSubmitBtn'
    ) as HTMLButtonElement | null;
    if (submitBtn) submitBtn.disabled = true;
    if (statusEl) {
      statusEl.textContent = 'Submitting...';
      statusEl.classList.remove('error');
    }
    try {
      await this.api.requestRetentionExtension({
        reason,
        requested_days: requestedDays,
      });
      if (statusEl) statusEl.textContent = 'Extension request submitted for super-admin review.';
      if (reasonEl) reasonEl.value = '';
      form?.classList.add('hidden');
      this.showToast('Extension request submitted for review.', 'success');
    } catch (error) {
      if (statusEl) {
        statusEl.textContent = (error as Error).message || 'Could not submit the request.';
        statusEl.classList.add('error');
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  private buildRetentionBannerMarkup(): string {
    if (!this.adminSessionActive || !this.selectedRequest?.image_url) return '';
    const state = this.computeRetentionState();
    if (!state.deletionDate || !Number.isFinite(state.daysLeft) || state.effectiveDays <= 0)
      return '';
    const days = Math.max(0, state.daysLeft);
    const dateLabel = state.deletionDate.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    // Request action is always available; only the urgency styling depends on how close deletion is.
    const nearExpiry = state.show;
    const tier = days <= 0 || days <= 7 ? 'critical' : nearExpiry ? 'soon' : 'ok';
    const heading =
      days <= 0
        ? 'Image no longer guaranteed'
        : nearExpiry
          ? `${days} day${days === 1 ? '' : 's'} of image access left`
          : `Image retention: ${state.effectiveDays} days`;
    const subText =
      days <= 0
        ? 'This screenshot is past its retention window and may be removed at any time. The text data for this record stays.'
        : nearExpiry
          ? `The screenshot stays available until ${this.escapeHtml(dateLabel)}. After that it is automatically removed and only the text data is kept.`
          : `The screenshot stays available until ${this.escapeHtml(dateLabel)}. Need it kept longer than the current limit? You can request an extension.`;
    const dayOptions = [15, 30, 45, 60, 90, 120, 180]
      .map((d) => `<option value="${d}"${d === 30 ? ' selected' : ''}>${d} days</option>`)
      .join('');
    return `
			<div class="retention-banner retention-${tier} detail-card" role="status">
				<div class="retention-head">
					<div>
						<p class="retention-title">${this.escapeHtml(heading)}</p>
						<p class="retention-sub">${subText}</p>
					</div>
					${days > 0 ? `<span class="retention-pill">Until ${this.escapeHtml(dateLabel)}</span>` : ''}
				</div>
				<button id="retentionRequestToggle" class="ghost retention-toggle" type="button">Request more retention time</button>
				<div id="retentionRequestForm" class="retention-form hidden">
					<label class="retention-field">
						<span>How many extra days do you need?</span>
						<select id="retentionDaysSelect" class="input">${dayOptions}</select>
					</label>
					<label class="retention-field">
						<span>Reason for the extension</span>
						<textarea id="retentionReasonInput" class="input" rows="3" placeholder="Why do you need the images kept longer?"></textarea>
					</label>
					<div class="action-buttons">
						<button id="retentionSubmitBtn" class="primary" type="button">Submit request</button>
						<button id="retentionCancelBtn" class="ghost" type="button">Cancel</button>
					</div>
					<p id="retentionRequestStatus" class="hint"></p>
				</div>
			</div>
		`;
  }

  private renderSelectedRequestDetail(): void {
    const detail = this.shadowRootRef.getElementById('requestDetail');
    if (!detail || !this.selectedRequest) return;
    const imageUrl = this.selectedRequest.image_url
      ? this.escapeHtml(this.selectedRequest.image_url)
      : '';
    const title = this.escapeHtml(this.selectedRequest.title || 'Request Detail');
    const requestId = this.escapeHtml(this.selectedRequest.id || '');
    const shortRequestId = requestId ? requestId.slice(0, 8) : '—';
    const statusText = this.escapeHtml(this.statusDisplayLabel(this.selectedRequest.status));
    const createdAt = this.formatTimestamp(this.selectedRequest.created_at);
    const updatedAt = this.formatTimestamp(
      this.selectedRequest.updated_at || this.selectedRequest.created_at
    );
    const submittedBy = this.escapeHtml(
      this.selectedRequest.submitted_by_email || this.selectedRequest.submitted_by || 'anonymous'
    );
    const description = this.escapeHtml(
      this.selectedRequest.description || 'No description provided.'
    );
    const pageLabel = this.escapeHtml(this.getRequestPageLabel(this.selectedRequest));
    const latestCommentText = this.getLatestAdminComment(this.selectedRequest);
    const latestCommentMarkup = latestCommentText
      ? this.escapeHtml(latestCommentText)
      : 'No admin comment yet. Status still shows the latest review state.';
    const statusHistory = this.buildStatusHistory(this.selectedRequest);
    const statusHistoryMarkup = statusHistory
      .map((entry) => {
        const label = this.escapeHtml(this.statusDisplayLabel(entry.status));
        const actor = this.escapeHtml(entry.by || 'system');
        const when = this.formatTimestamp(entry.at);
        return `
					<li class="activity-item">
						<div class="activity-head">
							<span class="status-chip ${this.statusClass(entry.status)}">${label}</span>
							<span>${actor}</span>
						</div>
						<div class="activity-note">${this.escapeHtml(when)}</div>
					</li>
				`;
      })
      .join('');
    const imageMarkup = imageUrl
      ? `
				<div class="request-image-card detail-card">
					<div class="request-image-head">
						<span>Submitted screenshot</span>
						<a class="ghost request-image-open" href="${imageUrl}" target="_blank" rel="noopener noreferrer">Open full</a>
					</div>
					<p class="request-detail-desc">${description}</p>
					<img class="request-image" src="${imageUrl}" alt="Submitted request screenshot" loading="lazy" />
				</div>
			`
      : `
				<div class="request-image-card detail-card">
					<div class="request-image-head">
						<span>Submitted screenshot</span>
					</div>
					<p class="request-detail-desc">${description}</p>
					<p class="hint">No screenshot available for this request.</p>
				</div>
			`;
    const adminControlsMarkup = this.adminSessionActive
      ? `
				<div class="request-action-card detail-card">
					<p class="request-action-title">Review Actions</p>
					<input id="adminCommentInput" class="input request-comment-input" placeholder="Comment for reject/comment action" />
					<div class="action-buttons">
						<button id="approveBtn" class="primary">Approve</button>
						<button id="rejectBtn" class="secondary">Reject</button>
						<button id="commentBtn" class="ghost">Comment</button>
						<button id="deleteBtn" class="ghost danger table-delete-btn" title="Delete request" aria-label="Delete request">
							<svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
								<path d="M9 3H15L16 5H21V7H3V5H8L9 3Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
								<path d="M6 7H18L17 19C16.92 20.1 16.01 21 14.9 21H9.1C7.99 21 7.08 20.1 7 19L6 7Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
								<path d="M10 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
								<path d="M14 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
							</svg>
						</button>
					</div>
				</div>
			`
      : `
				<div class="request-action-card detail-card readonly-review-card">
					<p class="request-action-title">Review Summary</p>
					<div class="readonly-review-list" role="list" aria-label="Read only review details">
						<div class="readonly-review-row" role="listitem">
							<span class="readonly-review-label">Mode</span>
							<span class="readonly-review-value">Read only</span>
						</div>
						<div class="readonly-review-row" role="listitem">
							<span class="readonly-review-label">Current status</span>
							<span class="readonly-review-value">${statusText}</span>
						</div>
						<div class="readonly-review-row" role="listitem">
							<span class="readonly-review-label">Last updated</span>
							<span class="readonly-review-value">${this.escapeHtml(updatedAt)}</span>
						</div>
					</div>
					<p class="readonly-review-note">Login with integration admin credentials to approve, reject, comment, or delete this request.</p>
				</div>
			`;
    const actionsMarkup = this.selectedRequest.actions
      .map((a) => {
        const actionBy = this.escapeHtml(a.performed_by || 'unknown');
        const actionText = a.action_text
          ? `<div class="activity-note">${this.escapeHtml(a.action_text)}</div>`
          : '';
        return `
					<li class="activity-item">
						<div class="activity-head">
							<span class="status-chip ${this.statusClass(a.action_type)}">${this.escapeHtml(a.action_type)}</span>
							<span>${actionBy}</span>
						</div>
						${actionText}
					</li>
				`;
      })
      .join('');
    detail.innerHTML = `
			<div class="request-detail-hero">
				<div class="request-detail-top">
					<button id="detailBackBtn" class="ghost icon-btn" type="button" aria-label="Back to table" title="Back to table">←</button>
					<span class="request-id-pill" title="Request ${requestId}">Request #${shortRequestId}</span>
					<span class="request-detail-tools">
						${imageUrl ? `<button id="detailExportImageBtn" class="ghost export-chip" type="button" title="Download image" aria-label="Download image">Image</button>` : ''}
						<button id="detailExportPdfBtn" class="ghost export-chip" type="button" title="Download data as PDF" aria-label="Download data as PDF">PDF</button>
						<button id="detailExportExcelBtn" class="ghost export-chip" type="button" title="Download data as Excel" aria-label="Download data as Excel">Excel</button>
					</span>
				</div>
				<div class="request-summary-row">
					<div class="request-summary-main">
						<h4 class="request-detail-title">${title}</h4>
						<span class="status-chip ${this.statusClass(this.selectedRequest.status)}">${statusText}</span>
					</div>
					<span class="request-summary-date">Created ${createdAt}</span>
				</div>
				<div class="request-meta-list" role="list" aria-label="Request summary details">
					<div class="request-meta-row" role="listitem">
						<span class="request-meta-label">Submitted by</span>
						<span class="request-meta-value">${submittedBy}</span>
					</div>
					<div class="request-meta-row" role="listitem">
						<span class="request-meta-label">Page</span>
						<span class="request-meta-value" title="${pageLabel}">${pageLabel}</span>
					</div>
					<div class="request-meta-row" role="listitem">
						<span class="request-meta-label">Last updated</span>
						<span class="request-meta-value">${this.escapeHtml(updatedAt)}</span>
					</div>
					<div class="request-meta-row" role="listitem">
						<span class="request-meta-label">Latest comment</span>
						<span class="request-meta-value">${latestCommentMarkup}</span>
					</div>
				</div>
			</div>
			<div class="request-detail-stack">
				${this.buildRetentionBannerMarkup()}
				${imageMarkup}
				${adminControlsMarkup}
				<div class="activity-card detail-card">
					<p class="request-action-title">Status history</p>
					<ul class="activity-list">${statusHistoryMarkup || '<li class="hint">No status changes yet</li>'}</ul>
				</div>
				<div class="activity-card detail-card">
					<p class="request-action-title">Recent activity</p>
					<ul class="activity-list">${actionsMarkup || '<li class="hint">No actions yet</li>'}</ul>
				</div>
			</div>
		`;
  }

  /** Build a flat list of label/value rows describing the selected request (for exports). */
  private buildSelectedRequestRows(): Array<{ label: string; value: string }> {
    const request = this.selectedRequest;
    if (!request) return [];
    const rows: Array<{ label: string; value: string }> = [
      { label: 'Request ID', value: request.id || '' },
      { label: 'Status', value: this.statusDisplayLabel(request.status) },
      { label: 'Title', value: request.title || '' },
      { label: 'Description', value: request.description || '' },
      { label: 'Submitted by', value: request.submitted_by_email || request.submitted_by || '' },
      { label: 'Page', value: this.getRequestPageLabel(request) },
      { label: 'Image URL', value: request.image_url || '' },
      { label: 'Created', value: this.formatTimestamp(request.created_at) },
      { label: 'Updated', value: this.formatTimestamp(request.updated_at || request.created_at) },
    ];
    const comment = this.getLatestAdminComment(request);
    if (comment) rows.push({ label: 'Latest comment', value: comment });
    return rows;
  }

  private sanitizeExportName(): string {
    const id = (this.selectedRequest?.id || 'request').replace(/[^a-z0-9-_]+/gi, '_');
    return `request-${id}`;
  }

  private openExportDialog(): void {
    const dialog = this.shadowRootRef.getElementById('exportDialog');
    if (!dialog) return;
    const message = this.shadowRootRef.getElementById('exportStatusMessage');
    if (message) {
      message.textContent = '';
      message.classList.remove('error');
    }
    dialog.classList.remove('hidden');
    dialog.setAttribute('aria-hidden', 'false');
  }

  private closeExportDialog(): void {
    const dialog = this.shadowRootRef.getElementById('exportDialog');
    if (!dialog) return;
    dialog.classList.add('hidden');
    dialog.setAttribute('aria-hidden', 'true');
  }

  /** Keep the "All statuses" checkbox mutually exclusive with the specific statuses. */
  private onExportStatusToggle(changed: HTMLInputElement): void {
    const boxes = Array.from(
      this.shadowRootRef.querySelectorAll('#exportStatusList [data-export-status]')
    ) as HTMLInputElement[];
    const allBox = boxes.find((b) => b.value === 'all');
    if (changed.value === 'all') {
      if (changed.checked) boxes.forEach((b) => (b.checked = b.value === 'all'));
      else if (!boxes.some((b) => b.value !== 'all' && b.checked)) changed.checked = true;
      return;
    }
    const anySpecific = boxes.some((b) => b.value !== 'all' && b.checked);
    if (allBox) allBox.checked = !anySpecific;
  }

  private getSelectedExportStatuses(): string[] {
    const boxes = Array.from(
      this.shadowRootRef.querySelectorAll('#exportStatusList [data-export-status]')
    ) as HTMLInputElement[];
    if (boxes.some((b) => b.value === 'all' && b.checked)) return [];
    return boxes.filter((b) => b.value !== 'all' && b.checked).map((b) => b.value);
  }

  /** Compute a human-readable retention label (days remaining before image deletion). */
  private computeRetentionLabel(item: WidgetRequestItem): string {
    const effectiveDays =
      this.retentionPolicy?.effective_retention_days ??
      (typeof item.image_retention_days === 'number' ? item.image_retention_days : 0);
    if (!Number.isFinite(effectiveDays) || effectiveDays <= 0) return 'N/A';
    const created = item.created_at ? new Date(item.created_at) : null;
    if (!created || Number.isNaN(created.getTime())) return `${effectiveDays}-day policy`;
    const deletionDate = new Date(created.getTime());
    deletionDate.setDate(deletionDate.getDate() + effectiveDays + 1);
    const daysLeft = Math.ceil((deletionDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    if (daysLeft <= 0) return `Expired (${effectiveDays}-day policy)`;
    return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left (${effectiveDays}-day policy)`;
  }

  /** Filter the loaded requests by the export dialog's date range + status selection. */
  private buildExportDataset(
    fromDate: string,
    toDate: string,
    statuses: string[]
  ): Array<{
    title: string;
    status: string;
    submittedBy: string;
    submittedDate: string;
    retention: string;
    comments: string;
    imageUrl: string;
  }> {
    const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
    const statusSet = statuses.length ? new Set(statuses) : null;
    return this.requests
      .filter((item) => {
        if (statusSet && !statusSet.has(item.status)) return false;
        if (fromTs !== null || toTs !== null) {
          const created = item.created_at ? new Date(item.created_at).getTime() : NaN;
          if (Number.isNaN(created)) return false;
          if (fromTs !== null && created < fromTs) return false;
          if (toTs !== null && created > toTs) return false;
        }
        return true;
      })
      .map((item) => ({
        title: item.title || item.description || `Request ${item.id}`,
        status: this.statusDisplayLabel(item.status),
        submittedBy: item.submitted_by_email || item.submitted_by || 'Unknown',
        submittedDate: this.formatTimestamp(item.created_at),
        retention: this.computeRetentionLabel(item),
        comments: (item.latest_comment || '').trim(),
        imageUrl: item.image_url || '',
      }));
  }

  /** Fetch an image and return a base64 data URL (for embedding), or null on failure. */
  private async fetchImageDataUrl(url: string): Promise<string | null> {
    let parsed: URL;
    try {
      parsed = new URL(url, window.location.href);
    } catch {
      return null;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    try {
      const response = await fetch(parsed.toString(), { credentials: 'include' });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) return null;
      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  private async runBulkExport(): Promise<void> {
    if (this.exportBusy) return;
    const message = this.shadowRootRef.getElementById('exportStatusMessage');
    const runBtn = this.shadowRootRef.getElementById('exportRunBtn') as HTMLButtonElement | null;
    const setMessage = (text: string, isError = false) => {
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('error', isError);
    };

    const formatEl = this.shadowRootRef.querySelector(
      'input[name="exportFormat"]:checked'
    ) as HTMLInputElement | null;
    const format = formatEl?.value === 'pdf' ? 'pdf' : 'excel';
    const fromDate =
      (this.shadowRootRef.getElementById('exportFromDate') as HTMLInputElement | null)?.value || '';
    const toDate =
      (this.shadowRootRef.getElementById('exportToDate') as HTMLInputElement | null)?.value || '';
    const includeImages =
      (this.shadowRootRef.getElementById('exportIncludeImages') as HTMLInputElement | null)
        ?.checked ?? false;

    if (fromDate && toDate && fromDate > toDate) {
      setMessage('The "From" date must be on or before the "To" date.', true);
      return;
    }

    const statuses = this.getSelectedExportStatuses();
    const dataset = this.buildExportDataset(fromDate, toDate, statuses);
    if (!dataset.length) {
      setMessage('No requests match the selected filters.', true);
      return;
    }

    this.exportBusy = true;
    if (runBtn) runBtn.disabled = true;

    try {
      let imageMap = new Map<string, string>();
      if (includeImages) {
        const urls = Array.from(new Set(dataset.map((r) => r.imageUrl).filter(Boolean)));
        if (urls.length) {
          setMessage(`Preparing ${urls.length} image${urls.length === 1 ? '' : 's'}...`);
          const results = await Promise.all(
            urls.map(async (u) => [u, await this.fetchImageDataUrl(u)] as const)
          );
          imageMap = new Map(results.filter((r): r is [string, string] => Boolean(r[1])));
        }
      }

      if (format === 'pdf') {
        this.exportAllToPdf(dataset, includeImages, imageMap);
      } else {
        this.exportAllToExcel(dataset, includeImages, imageMap);
      }
      setMessage(`Exported ${dataset.length} request${dataset.length === 1 ? '' : 's'}.`);
      this.showToast(`Exported ${dataset.length} request(s).`, 'success');
      this.closeExportDialog();
    } catch (error) {
      setMessage((error as Error).message || 'Export failed. Please try again.', true);
    } finally {
      this.exportBusy = false;
      if (runBtn) runBtn.disabled = false;
    }
  }

  private bulkExportFilename(ext: string): string {
    const stamp = new Date().toISOString().slice(0, 10);
    return `feedback-export-${stamp}.${ext}`;
  }

  /** Build the image cell HTML: small clickable thumbnail that opens the full image. */
  private exportImageCellHtml(imageUrl: string, imageMap: Map<string, string>, thumb = 56): string {
    if (!imageUrl) return '<span style="color:#90a0b4;">No image</span>';
    const thumbSrc = imageMap.get(imageUrl) || imageUrl;
    const href = this.escapeHtml(imageUrl);
    const img = `<img src="${this.escapeHtml(thumbSrc)}" alt="request image" width="${thumb}" style="max-width:${thumb}px;max-height:${thumb}px;object-fit:cover;border:1px solid #dbe4f0;border-radius:4px;" />`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" title="Open full image">${img}</a>`;
  }

  private exportAllToPdf(
    dataset: Array<{
      title: string;
      status: string;
      submittedBy: string;
      submittedDate: string;
      retention: string;
      comments: string;
      imageUrl: string;
    }>,
    includeImages: boolean,
    imageMap: Map<string, string>
  ): void {
    const headers = ['Title', 'Status', 'Submitted By', 'Submitted Date', 'Retention', 'Comments'];
    if (includeImages) headers.push('Image');
    const headHtml = headers
      .map(
        (h) =>
          `<th style="text-align:left;padding:6px 8px;background:#f3f5f8;">${this.escapeHtml(h)}</th>`
      )
      .join('');
    const bodyHtml = dataset
      .map((r) => {
        const cells = [
          this.escapeHtml(r.title),
          this.escapeHtml(r.status),
          this.escapeHtml(r.submittedBy),
          this.escapeHtml(r.submittedDate),
          this.escapeHtml(r.retention),
          this.escapeHtml(r.comments),
        ].map((c) => `<td style="padding:6px 8px;vertical-align:top;">${c}</td>`);
        if (includeImages)
          cells.push(
            `<td style="padding:6px 8px;vertical-align:top;text-align:center;">${this.exportImageCellHtml(r.imageUrl, imageMap)}</td>`
          );
        return `<tr>${cells.join('')}</tr>`;
      })
      .join('');
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>Feedback export</title>
			<style>body{font-family:Arial,Helvetica,sans-serif;color:#1b2b3b;margin:24px;}h1{font-size:18px;}table{border-collapse:collapse;width:100%;font-size:12px;border:1px solid #dbe4f0;}td,th{border:1px solid #dbe4f0;}</style>
			</head><body><h1>Feedback export</h1><p style="color:#607089;font-size:12px;">Exported ${this.escapeHtml(new Date().toLocaleString())} &middot; ${dataset.length} request(s)</p>
			<table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
			<script>window.onload=function(){setTimeout(function(){window.print();},300);};<\/script>
			</body></html>`;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=1000,height=720');
    if (!win) {
      this.showToast('Please allow pop-ups to download the PDF.', 'info');
      throw new Error('Pop-up blocked. Allow pop-ups to export as PDF.');
    }
    win.document.open();
    win.document.write(doc);
    win.document.close();
  }

  private exportAllToExcel(
    dataset: Array<{
      title: string;
      status: string;
      submittedBy: string;
      submittedDate: string;
      retention: string;
      comments: string;
      imageUrl: string;
    }>,
    includeImages: boolean,
    imageMap: Map<string, string>
  ): void {
    const headers = ['Title', 'Status', 'Submitted By', 'Submitted Date', 'Retention', 'Comments'];
    if (includeImages) headers.push('Image');
    const headHtml = headers.map((h) => `<th>${this.escapeHtml(h)}</th>`).join('');
    const bodyHtml = dataset
      .map((r) => {
        const cells = [
          this.escapeHtml(r.title),
          this.escapeHtml(r.status),
          this.escapeHtml(r.submittedBy),
          this.escapeHtml(r.submittedDate),
          this.escapeHtml(r.retention),
          this.escapeHtml(r.comments),
        ].map((c) => `<td>${c}</td>`);
        if (includeImages)
          cells.push(`<td>${this.exportImageCellHtml(r.imageUrl, imageMap, 64)}</td>`);
        return `<tr>${cells.join('')}</tr>`;
      })
      .join('');
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body>
			<table border="1"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
			</body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    this.triggerDownload(URL.createObjectURL(blob), this.bulkExportFilename('xls'), true);
  }

  /** Download the submitted screenshot as a file. Only http(s) URLs are followed. */
  private async downloadSelectedRequestImage(): Promise<void> {
    const url = this.selectedRequest?.image_url;
    if (!url) return;
    let parsed: URL;
    try {
      parsed = new URL(url, window.location.href);
    } catch {
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
    const ext =
      (parsed.pathname.split('.').pop() || 'png').replace(/[^a-z0-9]/gi, '').slice(0, 5) || 'png';
    try {
      const response = await fetch(parsed.toString(), { credentials: 'include' });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      this.triggerDownload(URL.createObjectURL(blob), `${this.sanitizeExportName()}.${ext}`, true);
      this.showToast('Image downloaded.', 'success');
    } catch {
      // Fallback: open directly so the browser can handle it.
      this.triggerDownload(parsed.toString(), `${this.sanitizeExportName()}.${ext}`, false);
    }
  }

  /** Export the request data as a PDF via a printable window (no bundled PDF library). */
  private exportSelectedRequestPdf(): void {
    const rows = this.buildSelectedRequestRows();
    if (!rows.length) return;
    const title = this.selectedRequest?.title || `Request ${this.selectedRequest?.id || ''}`;
    const rowsHtml = rows
      .map(
        (r) =>
          `<tr><th style="text-align:left;padding:6px 10px;vertical-align:top;white-space:nowrap;background:#f3f5f8;">${this.escapeHtml(r.label)}</th><td style="padding:6px 10px;vertical-align:top;">${this.escapeHtml(r.value)}</td></tr>`
      )
      .join('');
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${this.escapeHtml(title)}</title>
			<style>body{font-family:Arial,Helvetica,sans-serif;color:#1b2b3b;margin:24px;}h1{font-size:18px;}table{border-collapse:collapse;width:100%;font-size:13px;border:1px solid #dbe4f0;}td,th{border:1px solid #dbe4f0;}</style>
			</head><body><h1>${this.escapeHtml(title)}</h1><p style="color:#607089;font-size:12px;">Exported ${this.escapeHtml(new Date().toLocaleString())}</p>
			<table>${rowsHtml}</table>
			<script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
			</body></html>`;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=820,height=640');
    if (!win) {
      this.showToast('Please allow pop-ups to download the PDF.', 'info');
      return;
    }
    win.document.open();
    win.document.write(doc);
    win.document.close();
  }

  /** Export the request data as an Excel-compatible spreadsheet (no bundled library). */
  private exportSelectedRequestExcel(): void {
    const rows = this.buildSelectedRequestRows();
    if (!rows.length) return;
    const title = this.selectedRequest?.title || `Request ${this.selectedRequest?.id || ''}`;
    const bodyRows = rows
      .map(
        (r) => `<tr><td>${this.escapeHtml(r.label)}</td><td>${this.escapeHtml(r.value)}</td></tr>`
      )
      .join('');
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body>
			<table border="1"><thead><tr><th colspan="2">${this.escapeHtml(title)}</th></tr><tr><th>Field</th><th>Value</th></tr></thead><tbody>${bodyRows}</tbody></table>
			</body></html>`;
    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    this.triggerDownload(URL.createObjectURL(blob), `${this.sanitizeExportName()}.xls`, true);
    this.showToast('Excel file downloaded.', 'success');
  }

  private triggerDownload(href: string, filename: string, revoke: boolean): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.rel = 'noopener';
    if (!revoke) link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
    if (revoke) setTimeout(() => URL.revokeObjectURL(href), 1000);
  }

  /** Read the captured page/location from a request's metadata. */
  private getRequestPageLabel(request: WidgetRequestItem): string {
    const metadata = request.metadata;
    if (metadata && typeof metadata === 'object') {
      const candidateKeys = ['page', 'page_label', 'page_chain', 'page_title'];
      for (const key of candidateKeys) {
        const value = (metadata as Record<string, unknown>)[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    }
    return 'Unknown';
  }

  private getLatestAdminComment(request: WidgetRequestDetail): string | null {
    for (let i = request.actions.length - 1; i >= 0; i -= 1) {
      const action = request.actions[i];
      const text = action.action_text?.trim();
      if (!text) continue;
      if (action.action_type === 'submitted') continue;
      return text;
    }
    return null;
  }

  private buildStatusHistory(
    request: WidgetRequestDetail
  ): Array<{ status: WidgetRequestItem['status']; by: string; at?: string | null }> {
    const history: Array<{ status: WidgetRequestItem['status']; by: string; at?: string | null }> =
      [];
    let lastStatus: WidgetRequestItem['status'] | undefined;

    const pushStatus = (status: WidgetRequestItem['status'], by: string, at?: string | null) => {
      if (lastStatus === status) return;
      history.push({ status, by, at });
      lastStatus = status;
    };

    request.actions.forEach((action) => {
      const actor = action.performed_by || 'system';
      if (action.action_type === 'submitted') {
        pushStatus('submitted', actor, action.created_at);
        return;
      }
      if (action.action_type === 'approve') {
        pushStatus('approved', actor, action.created_at);
        return;
      }
      if (action.action_type === 'reject') {
        pushStatus('rejected', actor, action.created_at);
        return;
      }
      if (action.action_type === 'comment' && action.action_text) {
        const match = action.action_text.match(/status changed from\s+([a-z_]+)\s+to\s+([a-z_]+)/i);
        const next = match?.[2]?.toLowerCase();
        if (
          next === 'submitted' ||
          next === 'in_review' ||
          next === 'needs_info' ||
          next === 'approved' ||
          next === 'rejected'
        ) {
          pushStatus(next, actor, action.created_at);
        }
      }
    });

    const current = request.status;
    if (lastStatus !== current) {
      pushStatus(current, 'current state', request.updated_at || request.created_at);
    }

    return history;
  }

  private statusDisplayLabel(status: WidgetRequestItem['status']): string {
    if (status === 'submitted') return 'Submitted';
    if (status === 'in_review') return 'In review';
    if (status === 'needs_info') return 'Needs info';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    return status;
  }

  private openDeleteConfirmation(requestId: string): Promise<boolean> {
    const dialog = this.shadowRootRef.getElementById('deleteConfirmDialog');
    const message = this.shadowRootRef.getElementById('deleteConfirmMessage');
    const matchingRequest = this.requests.find((item) => item.id === requestId);
    const requestLabel = this.escapeHtml(matchingRequest?.title?.trim() || requestId);

    if (message) {
      message.innerHTML = `This request will be removed from both dashboard views.<br><strong>${requestLabel}</strong>`;
    }

    dialog?.classList.remove('hidden');
    dialog?.setAttribute('aria-hidden', 'false');

    return new Promise<boolean>((resolve) => {
      this.deleteConfirmResolver = resolve;
    });
  }

  private resolveDeleteConfirmation(confirmed: boolean): void {
    const dialog = this.shadowRootRef.getElementById('deleteConfirmDialog');
    dialog?.classList.add('hidden');
    dialog?.setAttribute('aria-hidden', 'true');

    if (this.deleteConfirmResolver) {
      this.deleteConfirmResolver(confirmed);
      this.deleteConfirmResolver = undefined;
    }
  }

  private async deleteRequestById(requestId: string): Promise<void> {
    if (!this.api || !this.adminSessionActive) return;
    const confirmed = await this.openDeleteConfirmation(requestId);
    if (!confirmed) return;

    const adminStatus = this.shadowRootRef.getElementById('adminStatus');
    try {
      await this.api.deleteWidgetRequest(requestId);
      this.requests = this.requests.filter((item) => item.id !== requestId);
      if (this.selectedRequestId === requestId) {
        this.selectedRequestId = undefined;
        this.selectedRequest = undefined;
      }
      this.ensureVisibleSelection();
      this.renderRequesterChips();
      this.renderRequestList();
      this.updateRequestsTabBadge();
      if (this.selectedRequestId) {
        await this.loadRequestDetail(this.selectedRequestId);
        this.setDashboardTab('detail');
      } else {
        const detail = this.shadowRootRef.getElementById('requestDetail');
        if (detail)
          detail.innerHTML = '<p class="hint">No matching requests for the current filters.</p>';
        this.setDashboardTab('table');
      }
      this.publishSyncMessage({ type: 'requests-refresh', requestId, ts: Date.now() });
      if (adminStatus) adminStatus.textContent = 'Request removed from dashboard views.';
      this.showToast('Request removed.', 'success');
      void this.refreshRequestsInBackground();
    } catch (error) {
      if (adminStatus) adminStatus.textContent = `Delete failed: ${(error as Error).message}`;
      this.showToast(`Delete failed: ${(error as Error).message}`, 'error');
    }
  }

  private async updateStatusFromTable(
    requestId: string,
    nextStatus: WidgetRequestItem['status']
  ): Promise<void> {
    if (!this.api || !this.adminSessionActive) return;
    const adminStatus = this.shadowRootRef.getElementById('adminStatus');
    try {
      if (!this.adminActionToken) {
        const verify = await this.api.adminVerify();
        this.adminActionToken = verify.token;
      }
      await this.applyStatusUpdateWithRetry(requestId, nextStatus, false);
      const now = new Date().toISOString();
      const rowIndex = this.requests.findIndex((item) => item.id === requestId);
      if (rowIndex >= 0) {
        this.requests[rowIndex] = {
          ...this.requests[rowIndex],
          status: nextStatus,
          updated_at: now,
        };
      }
      if (this.selectedRequest && this.selectedRequest.id === requestId) {
        this.selectedRequest.status = nextStatus;
        this.selectedRequest.updated_at = now;
        this.renderSelectedRequestDetail();
      }
      this.renderRequestList();
      this.publishSyncMessage({ type: 'request-updated', requestId, ts: Date.now() });
      if (adminStatus) adminStatus.textContent = `Status updated to ${nextStatus}.`;
      this.showToast(`Status updated to ${nextStatus}.`, 'success');
    } catch (error) {
      if (adminStatus)
        adminStatus.textContent = `Status update failed: ${(error as Error).message}`;
      this.showToast(`Status update failed: ${(error as Error).message}`, 'error');
      void this.refreshRequestsInBackground();
    }
  }

  private async applyStatusUpdateWithRetry(
    requestId: string,
    nextStatus: WidgetRequestItem['status'],
    retriedAfterVerify: boolean
  ): Promise<void> {
    if (!this.api || !this.adminActionToken) {
      throw new Error('Admin session missing');
    }
    try {
      await this.api.updateRequestStatus(requestId, nextStatus, this.adminActionToken);
    } catch (error) {
      const message = (error as Error).message.toLowerCase();
      const shouldRefreshVerification =
        message.includes('verification required') ||
        message.includes('invalid admin action token') ||
        message.includes('x-admin-action-token is required');
      if (!retriedAfterVerify && shouldRefreshVerification) {
        const verify = await this.api.adminVerify();
        this.adminActionToken = verify.token;
        await this.applyStatusUpdateWithRetry(requestId, nextStatus, true);
        return;
      }
      throw error;
    }
  }

  private applyLocalRequestMutation(
    actionType: 'approve' | 'reject' | 'comment',
    comment?: string
  ): void {
    if (!this.selectedRequest) return;
    const now = new Date().toISOString();
    if (actionType === 'approve') {
      this.selectedRequest.status = 'approved';
    }
    if (actionType === 'reject') {
      this.selectedRequest.status = 'rejected';
    }
    this.selectedRequest.actions = [
      ...this.selectedRequest.actions,
      {
        id: `local-${Date.now()}`,
        action_type: actionType,
        action_text: comment || null,
        performed_by: this.adminEmail,
        created_at: now,
      },
    ];

    const index = this.requests.findIndex((item) => item.id === this.selectedRequest?.id);
    if (index >= 0) {
      this.requests[index] = {
        ...this.requests[index],
        status: this.selectedRequest.status,
        latest_comment:
          actionType === 'comment' ? comment || null : this.requests[index].latest_comment,
        updated_at: now,
      };
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private formatTimestamp(value?: string | null): string {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }

  private formatTimestampCompact(value?: string | null): string {
    if (!value) return 'Unknown';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
  }

  private statusClass(status: string): string {
    const normalized = status.toLowerCase();
    if (normalized.includes('approve')) return 'status-approved';
    if (normalized.includes('reject')) return 'status-rejected';
    if (
      normalized.includes('needs_info') ||
      normalized.includes('submitted') ||
      normalized.includes('review')
    )
      return 'status-pending';
    if (normalized.includes('comment')) return 'status-comment';
    if (normalized.includes('pending')) return 'status-pending';
    return 'status-generic';
  }

  private async loadCategories(fallback: FeedbackCategory[]): Promise<void> {
    const select = this.shadowRootRef.getElementById('categorySelect') as HTMLSelectElement | null;
    if (!select) return;
    const previousValue = select.value;

    try {
      const list = await this.api?.getCategories();
      if (list && list.length > 0) {
        this.categories = list.map((x) => ({ id: x.id, label: x.name }));
      } else {
        this.categories = fallback;
      }
    } catch {
      this.categories = fallback;
    }

    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'General';
    select.appendChild(defaultOption);

    this.categories.forEach((category) => {
      const option = document.createElement('option');
      option.value = category.id || '';
      option.textContent = category.label;
      select.appendChild(option);
    });

    const otherOption = document.createElement('option');
    otherOption.value = '__other__';
    otherOption.textContent = 'Other';
    select.appendChild(otherOption);

    if (
      previousValue &&
      Array.from(select.options).some((option) => option.value === previousValue)
    ) {
      select.value = previousValue;
    }
    this.updateCategoryOtherVisibility();
  }

  private updateCategoryOtherVisibility(): void {
    const select = this.shadowRootRef.getElementById('categorySelect') as HTMLSelectElement | null;
    const otherInput = this.shadowRootRef.getElementById(
      'categoryOtherInput'
    ) as HTMLInputElement | null;
    if (!select || !otherInput) return;
    if (select.value === '__other__') {
      otherInput.classList.remove('hidden');
    } else {
      otherInput.classList.add('hidden');
      otherInput.value = '';
    }
  }

  private async canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | undefined> {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ?? undefined;
  }

  private getDocumentCaptureSize(): { width: number; height: number } {
    const doc = document.documentElement;
    const body = document.body;
    const width = Math.max(
      doc.scrollWidth,
      doc.offsetWidth,
      doc.clientWidth,
      body?.scrollWidth || 0,
      body?.offsetWidth || 0,
      body?.clientWidth || 0
    );
    const height = Math.max(
      doc.scrollHeight,
      doc.offsetHeight,
      doc.clientHeight,
      body?.scrollHeight || 0,
      body?.offsetHeight || 0,
      body?.clientHeight || 0
    );

    return {
      width: Math.max(1, Math.floor(width)),
      height: Math.max(1, Math.floor(height)),
    };
  }

  private getCaptureScale(mode: 'viewport' | 'full', width: number, height: number): number {
    const maxPixels = mode === 'full' ? 2_200_000 : 1_600_000;
    const area = Math.max(1, width * height);
    const rawScale = Math.sqrt(maxPixels / area);
    // Keep enough detail while reducing expensive raster work on large/complex pages.
    return Math.max(0.4, Math.min(1, rawScale));
  }

  private async waitForNextFrame(): Promise<void> {
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  }

  private async withTimeout<T>(work: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    });
    try {
      return await Promise.race([work, timeoutPromise]);
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    }
  }

  private async capturePageCanvas(
    mode: 'viewport' | 'full' = 'viewport'
  ): Promise<HTMLCanvasElement> {
    const host = this;
    const previousVisibility = host.style.visibility;
    const previousPointerEvents = host.style.pointerEvents;

    host.style.visibility = 'hidden';
    host.style.pointerEvents = 'none';

    try {
      const ignoreElements = (el: Element) =>
        el === host ||
        el.tagName === 'SCRIPT' ||
        el.tagName === 'NOSCRIPT' ||
        el.tagName === 'IFRAME' ||
        el.tagName === 'VIDEO';

      await this.waitForNextFrame();

      if (mode === 'full') {
        const { width: rawDocWidth, height: rawDocHeight } = this.getDocumentCaptureSize();
        const docWidth = Math.min(rawDocWidth, 5000);
        const docHeight = Math.min(rawDocHeight, 8000);
        const scale = this.getCaptureScale('full', docWidth, docHeight);
        return await this.withTimeout(
          html2canvas(document.documentElement, {
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: docWidth,
            height: docHeight,
            windowWidth: docWidth,
            windowHeight: docHeight,
            scrollX: 0,
            scrollY: 0,
            scale,
            ignoreElements,
            imageTimeout: 1200,
            removeContainer: true,
            foreignObjectRendering: false,
          }),
          10000,
          'Capture timed out'
        );
      }

      // Viewport mode: capture only the visible viewport area.
      // Use the document element + its client (layout) dimensions so the
      // crop stays aligned even when the window is snapped/split (where
      // window.innerWidth includes the scrollbar and would offset the
      // region, producing a blank capture).
      const docEl = document.documentElement;
      const scroller = document.scrollingElement || docEl;
      const vpWidth = Math.max(1, docEl.clientWidth || window.innerWidth);
      const vpHeight = Math.max(1, docEl.clientHeight || window.innerHeight);
      const scrollX = scroller.scrollLeft || window.scrollX || 0;
      const scrollY = scroller.scrollTop || window.scrollY || 0;
      const scale = this.getCaptureScale('viewport', vpWidth, vpHeight);
      return await this.withTimeout(
        html2canvas(docEl, {
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: vpWidth,
          height: vpHeight,
          windowWidth: vpWidth,
          windowHeight: vpHeight,
          scrollX,
          scrollY,
          x: scrollX,
          y: scrollY,
          scale,
          ignoreElements,
          imageTimeout: 1200,
          removeContainer: true,
          foreignObjectRendering: false,
        }),
        8000,
        'Capture timed out'
      );
    } finally {
      host.style.visibility = previousVisibility;
      host.style.pointerEvents = previousPointerEvents;
    }
  }

  private async captureScreenshot(): Promise<void> {
    const status = this.shadowRootRef.getElementById('captureState');
    const captureBtn = this.shadowRootRef.getElementById('captureBtn') as HTMLButtonElement | null;
    if (!status) return;
    if (this.isCapturingScreenshot) return;
    this.isCapturingScreenshot = true;
    if (captureBtn) captureBtn.disabled = true;
    status.textContent = 'Capturing...';

    try {
      if (this.captureMode === 'full') {
        const fullCanvas = await this.capturePageCanvas('full');
        this.screenshotBlob = await this.canvasToBlob(fullCanvas);
        status.textContent = this.screenshotBlob
          ? 'Full page screenshot attached (widget excluded)'
          : 'Could not attach screenshot';
        return;
      }

      const pageCanvas = await this.capturePageCanvas('viewport');

      if (this.captureMode === 'old-basic') {
        this.screenshotBlob = await this.canvasToBlob(pageCanvas);
        status.textContent = this.screenshotBlob
          ? 'Screenshot attached (basic viewport)'
          : 'Could not attach screenshot';
        return;
      }

      if (this.captureMode === 'snippet') {
        status.textContent = 'Select an area to capture';
        this.openSnippetSelector(pageCanvas);
        return;
      }

      this.annotTool = 'pen';
      status.textContent = 'Annotate and click Done';
      this.openAnnotationEditor(pageCanvas);
    } catch (err) {
      this.screenshotBlob = undefined;
      // Surface the real cause; the generic "timed out" text hid failures
      // like unsupported CSS colours (oklch/lab) or CORS-blocked images.
      console.error('[FaaS widget] Screenshot capture failed:', err);

      // Self-recover: try once more in the most tolerant mode (basic
      // viewport) before giving up, so a single slow/complex frame or the
      // interactive modes failing doesn't dead-end the user.
      try {
        status.textContent = 'Retrying capture…';
        const fallbackCanvas = await this.capturePageCanvas('viewport');
        this.screenshotBlob = await this.canvasToBlob(fallbackCanvas);
        if (this.screenshotBlob) {
          status.textContent = 'Screenshot attached (basic viewport)';
          return;
        }
      } catch (retryErr) {
        console.error('[FaaS widget] Fallback capture also failed:', retryErr);
      }

      this.screenshotBlob = undefined;
      status.textContent =
        'Couldn’t capture the screen. You can attach an image manually or submit without one.';
    } finally {
      this.isCapturingScreenshot = false;
      if (captureBtn) captureBtn.disabled = false;
    }
  }

  private handleUploadedFile(file: File): void {
    const captureState = this.shadowRootRef.getElementById('captureState');
    const uploadFile = this.shadowRootRef.getElementById('uploadFile') as HTMLInputElement | null;
    const dropZone = this.shadowRootRef.getElementById('uploadDropZone');
    const maxBytes = 5 * 1024 * 1024;
    if (!file.type.startsWith('image/')) {
      this.showToast('Only image files are accepted.', 'error');
      if (uploadFile) uploadFile.value = '';
      return;
    }
    if (file.size > maxBytes) {
      this.showToast('Image must be smaller than 5 MB.', 'error');
      if (uploadFile) uploadFile.value = '';
      return;
    }
    this.screenshotBlob = file;
    if (captureState) captureState.textContent = `${file.name} attached ✓`;
    dropZone?.setAttribute('data-has-file', 'true');
  }

  private async submit(): Promise<void> {
    const textInput = this.shadowRootRef.getElementById(
      'feedbackText'
    ) as HTMLTextAreaElement | null;
    const status = this.shadowRootRef.getElementById('statusMsg') as HTMLParagraphElement | null;
    const submitBtn = this.shadowRootRef.getElementById('submitBtn') as HTMLButtonElement | null;
    if (!textInput || !status || !submitBtn || !this.api) return;
    this.applyOptionalUserEmailContext();
    const userEmailInput = this.shadowRootRef.getElementById(
      'userEmailInput'
    ) as HTMLInputElement | null;
    const enteredEmail = userEmailInput?.value.trim().toLowerCase() || '';
    if (!enteredEmail) {
      status.textContent = 'Email is required. Please enter your email before submitting.';
      status.classList.add('error');
      this.showToast('Email is required.', 'error');
      userEmailInput?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enteredEmail)) {
      status.textContent = 'Please enter a valid email address.';
      status.classList.add('error');
      this.showToast('Please enter a valid email address.', 'error');
      userEmailInput?.focus();
      return;
    }
    const submittedEmail = enteredEmail;

    const text = textInput.value.trim();
    if (!text) {
      status.textContent = 'Please add feedback text before submitting.';
      status.classList.add('error');
      this.showToast('Feedback text is required.', 'error');
      return;
    }

    const categorySelect = this.shadowRootRef.getElementById(
      'categorySelect'
    ) as HTMLSelectElement | null;
    const categoryOtherInput = this.shadowRootRef.getElementById(
      'categoryOtherInput'
    ) as HTMLInputElement | null;
    let selectedCategoryId = '';
    let selectedCategoryLabel = '';
    if (categorySelect) {
      if (categorySelect.value === '__other__') {
        const customCategory = categoryOtherInput?.value.trim() || '';
        if (!customCategory) {
          status.textContent = 'Please enter a category before submitting.';
          status.classList.add('error');
          this.showToast('Category is required when "Other" is selected.', 'error');
          categoryOtherInput?.focus();
          return;
        }
        selectedCategoryLabel = customCategory;
      } else if (categorySelect.value) {
        selectedCategoryId = categorySelect.value;
        selectedCategoryLabel = categorySelect.options[categorySelect.selectedIndex]?.text || '';
      } else {
        selectedCategoryLabel = categorySelect.options[categorySelect.selectedIndex]?.text || '';
      }
    }

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    submitBtn.disabled = true;
    status.textContent = 'Submitting feedback...';
    status.classList.remove('error');

    try {
      const nonAdminContext: Record<string, unknown> = {
        user_id: this.getAttribute('user-id') || 'widget-user',
        user_email: submittedEmail,
        user_role: this.getAttribute('user-role') || 'user',
        user_agent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        page: this.getPageChain(),
      };
      if (this.selectedRating > 0) {
        nonAdminContext.rating = this.selectedRating;
      }

      if (selectedCategoryLabel) {
        nonAdminContext.category = selectedCategoryLabel;
      }
      if (selectedCategoryId) {
        nonAdminContext.category_id = selectedCategoryId;
      }

      const response = await this.api.createWidgetRequest({
        title: text.slice(0, 80),
        description: text,
        screenshot: this.screenshotBlob,
        metadata: nonAdminContext,
        sourceUrl: window.location.href,
        pageTitle: document.title,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
      this.publishSyncMessage({ type: 'request-created', requestId: response.id, ts: Date.now() });
      status.textContent = `Submitted. Request: ${response.id}`;
      this.showToast('Request submitted successfully.', 'success');
      void this.api.trackEvent('feedback_submitted', 'submit', {
        request_id: response.id,
        category: selectedCategoryLabel || null,
        rating: this.selectedRating > 0 ? this.selectedRating : null,
        capture_mode: this.captureMode,
      });
      textInput.value = '';
      this.setRating(0);
      this.screenshotBlob = undefined;
      if (categorySelect) categorySelect.value = '';
      this.updateCategoryOtherVisibility();
      const captureState = this.shadowRootRef.getElementById('captureState');
      if (captureState) captureState.textContent = 'No screenshot attached';
      const uploadFile = this.shadowRootRef.getElementById('uploadFile') as HTMLInputElement | null;
      if (uploadFile) uploadFile.value = '';
      this.shadowRootRef.getElementById('uploadDropZone')?.removeAttribute('data-has-file');
      this.switchTab('requests');
      await this.loadRequestsView();
    } catch (error) {
      status.textContent = `Submission failed: ${(error as Error).message}`;
      status.classList.add('error');
      this.showToast(`Submission failed: ${(error as Error).message}`, 'error');
    } finally {
      this.isSubmitting = false;
      submitBtn.disabled = false;
    }
  }

  private bindRatingEvents(): void {
    const stars = this.shadowRootRef.querySelectorAll<HTMLButtonElement>('[data-rating-star]');
    stars.forEach((starButton) => {
      const starValue = Number(starButton.dataset.ratingStar || '0');
      if (!Number.isFinite(starValue) || starValue < 1 || starValue > 5) return;

      starButton.addEventListener('mouseenter', () => this.updateRatingUI(starValue));
      starButton.addEventListener('focus', () => this.updateRatingUI(starValue));
      starButton.addEventListener('mouseleave', () => this.updateRatingUI());
      starButton.addEventListener('blur', () => this.updateRatingUI());
      starButton.addEventListener('click', () => this.setRating(starValue));
      starButton.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setRating(starValue);
        }
      });
    });
  }

  private setRating(value: number): void {
    const normalized = Math.max(0, Math.min(5, Math.floor(value)));
    this.selectedRating = normalized;
    this.updateRatingUI();
  }

  private updateRatingUI(hoverValue = 0): void {
    const current = hoverValue > 0 ? hoverValue : this.selectedRating;
    const stars = this.shadowRootRef.querySelectorAll<HTMLButtonElement>('[data-rating-star]');
    stars.forEach((starButton) => {
      const starValue = Number(starButton.dataset.ratingStar || '0');
      const isActive = starValue <= current;
      starButton.classList.toggle('active', isActive);
      starButton.setAttribute('aria-checked', this.selectedRating === starValue ? 'true' : 'false');
    });
  }

  /**
   * Derive a human-readable "page" label for the host page.
   * Prefers a breadcrumb chain (e.g. "Client > Info > Details"), then the
   * main heading, then the document title, then the pathname.
   */
  private getPageChain(): string {
    try {
      const explicit = (this.getAttribute('page-label') || '').trim();
      if (explicit) return explicit;

      const breadcrumb = this.extractBreadcrumbChain();
      if (breadcrumb) return breadcrumb;

      const heading = document.querySelector('h1')?.textContent?.trim();
      if (heading) return heading;

      const title = document.title?.trim();
      if (title) return title;

      const path = window.location.pathname.replace(/\/+$/, '');
      if (path && path !== '/') {
        return path
          .split('/')
          .filter(Boolean)
          .map((segment) => decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim())
          .join(' > ');
      }
    } catch {
      /* ignore and fall through */
    }
    return 'Home';
  }

  /**
   * Try to read a breadcrumb trail from common markup patterns and join it
   * with " > " (e.g. "Client > Info > Details").
   */
  private extractBreadcrumbChain(): string {
    const selectors = [
      'nav[aria-label="breadcrumb" i]',
      '[role="navigation"][aria-label="breadcrumb" i]',
      'ol.breadcrumb',
      'ul.breadcrumb',
      '.breadcrumbs',
      '.breadcrumb',
      '[itemtype*="BreadcrumbList" i]',
    ];

    for (const selector of selectors) {
      const container = document.querySelector(selector);
      if (!container) continue;

      let items = Array.from(container.querySelectorAll('li'));
      if (items.length === 0) {
        items = Array.from(container.querySelectorAll('a, span'));
      }

      const parts = items
        .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
        .filter((text) => text.length > 0 && !/^[>/\\|»›\u2022]+$/.test(text));

      const chain = parts.filter((text, index) => text !== parts[index - 1]);
      if (chain.length > 0) return chain.join(' > ');
    }
    return '';
  }

  /** Populate the read-only "Page" field with the current page chain. */
  private refreshPageContextField(): void {
    const input = this.shadowRootRef.getElementById('pageContextInput') as HTMLInputElement | null;
    if (input) input.value = this.getPageChain();
  }

  private applyOptionalUserEmailContext(): void {
    if (!this.api) return;
    const context = this.readUserContextFromAttributes();
    const userEmailInput = this.shadowRootRef.getElementById(
      'userEmailInput'
    ) as HTMLInputElement | null;
    const typedEmail = userEmailInput?.value.trim().toLowerCase();
    this.api.setUserContext({
      userId: context.userId,
      userEmail: typedEmail || context.userEmail,
      userRole: context.userRole,
    });

    const tenantKey = this.getAttribute('tenant-key') || 'local-tenant';
    const nextSeenKey = this.buildSeenUpdatesStorageKey(
      tenantKey,
      context.userId,
      typedEmail || context.userEmail
    );
    if (nextSeenKey !== this.seenUpdatesStorageKey) {
      this.seenUpdatesStorageKey = nextSeenKey;
      this.seenRequestUpdates = {};
      this.seenUpdatesInitialized = false;
      this.loadSeenRequestUpdates();
    }
  }
}

if (!customElements.get(tagName)) {
  customElements.define(tagName, FeedbackWidget);
}

export { FeedbackWidget };
